import { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api, { mediaSrc, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import {
  Trash2, Users, Film, ImageIcon, MessageCircle, Layers, Loader2, Shield, ShieldCheck,
  Star, EyeOff, Eye, Lock, LockOpen, Search, UserX, UserCheck, Crown, Ban, Hammer, Sparkles, Type, Files, CheckSquare, Square, RefreshCw,
  AlertTriangle, ExternalLink, Moon,
  Cpu, Server, HardDrive, Clock, Network, Activity, Archive, Check, Wand2, StopCircle, Pencil, TrendingUp, BarChart3, Heart,
  Download, FileText, BookOpen, X, Maximize2, ImagePlus, Megaphone,
  BadgeCheck,
} from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../components/ui/switch";
import { AdminNav } from "../components/AdminNav";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { AreaChart, Area, LineChart, Line, ResponsiveContainer, YAxis, Tooltip } from "recharts";
import { VerifiedBadge } from "../components/VerifiedBadge";
import { VerificationQueue } from "../components/VerificationQueue";

const NAV_GROUPS = [
  { label: "Dashboard", tabs: [
    { id: "overview", label: "Overview", icon: Layers },
    { id: "insights", label: "Insights", icon: BarChart3 },
  ] },
  { label: "Library", tabs: [
    { id: "content", label: "Content & AI", icon: Film },
    { id: "duplicates", label: "Duplicates", icon: Files },
    { id: "upscale", label: "Upscale", icon: Wand2 },
    { id: "import", label: "Import", icon: Download },
  ] },
  { label: "Community", tabs: [
    { id: "members", label: "Members", icon: Users },
    { id: "safety", label: "Safety", icon: ShieldCheck, adminOnly: true },
  ] },
  { label: "Platform", tabs: [
    { id: "ksi", label: "Search index", icon: Network },
    { id: "system", label: "System", icon: Activity },
  ] },
];

export default function Admin() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);

  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch(() => {}); }, []);

  const groups = NAV_GROUPS.map((g) => ({ ...g, tabs: g.tabs.filter((t) => !t.adminOnly || isAdmin) })).filter((g) => g.tabs.length);
  const current = groups.flatMap((g) => g.tabs).find((t) => t.id === tab);

  return (
    <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12">
      <div className="flex items-center gap-3 mb-6">
        <Shield className="text-[#0066FF]" size={24} />
        <h1 className="font-heading text-2xl sm:text-3xl tracking-tight text-white">{isAdmin ? "Admin" : "Moderation"}</h1>
        {current && <span className="text-[#64748B] text-sm" data-testid="admin-current-section">/ {current.label}</span>}
      </div>

      <div className="flex flex-col md:flex-row gap-6" data-testid="admin-tabs">
        <AdminNav groups={groups} value={tab} onChange={setTab} />
        <main className="flex-1 min-w-0">
      {tab === "overview" && <Overview stats={stats} />}
      {tab === "insights" && <InsightsPanel />}
      {tab === "content" && <Content />}
      {tab === "duplicates" && <Duplicates />}
      {tab === "members" && <Members isAdmin={isAdmin} currentUserId={user?.id} />}
      {tab === "upscale" && <UpscalePanel />}
      {tab === "import" && <DiscordImportPanel />}
      {tab === "ksi" && <KsiPanel />}
      {tab === "safety" && isAdmin && <SafetyPanel />}
      {tab === "system" && <SystemPanel />}
        </main>
      </div>
    </div>
  );
}

function Overview({ stats }) {
  if (!stats) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0066FF]" size={28} /></div>;
  const cards = [
    { label: "Users", value: stats.users, icon: Users },
    { label: "Admins", value: stats.admins, icon: Crown },
    { label: "Moderators", value: stats.moderators, icon: ShieldCheck },
    { label: "Banned", value: stats.banned, icon: Ban },
    { label: "Media", value: stats.media, icon: Layers },
    { label: "Videos", value: stats.videos, icon: Film },
    { label: "Photos", value: stats.images, icon: ImageIcon },
    { label: "Featured", value: stats.featured, icon: Star },
    { label: "Hidden", value: stats.hidden, icon: EyeOff },
    { label: "Comments", value: stats.comments, icon: MessageCircle },
  ];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
      {cards.map((c) => (
        <div key={c.label} data-testid={`stat-${c.label.toLowerCase()}`} className="border border-white/10 bg-[#182132] rounded-xl p-5">
          <c.icon className="text-[#0066FF] mb-3" size={20} />
          <p className="font-heading text-4xl text-white leading-none">{c.value ?? 0}</p>
          <p className="font-mono text-[10px] text-[#52525B] uppercase tracking-wider mt-1">{c.label}</p>
        </div>
      ))}
    </div>
  );
}

function InsightsPanel() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get("/admin/insights").then(({ data }) => setD(data)).catch(() => setErr(true)); }, []);
  if (err) return <p className="text-[#ff3b5c] text-sm" data-testid="insights-error">Couldn't load insights.</p>;
  if (!d) return <div className="flex items-center gap-2 text-[#52525B] text-sm py-10"><Loader2 className="animate-spin" size={16} /> Crunching the numbers…</div>;
  const growthSeries = d.signups_14d.map((s, i) => ({ t: s.date, signups: s.n, uploads: d.uploads_14d[i]?.n || 0 }));
  const maxCat = Math.max(1, ...d.categories.map((c) => c.count));
  return (
    <div className="space-y-4" data-testid="admin-insights-panel">
      <SectionHead title="Growth" hint="new members, media & comments over time" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="New Members" value={d.growth.users_7d} icon={Users} explain={`${d.growth.users_30d} joined in the last 30 days`} sub="last 7 days" />
        <StatCard label="New Media" value={d.growth.media_7d} icon={Film} explain={`${d.growth.media_30d} added in the last 30 days`} sub="last 7 days" />
        <StatCard label="New Comments" value={d.growth.comments_7d} icon={MessageCircle} explain="Community engagement." sub="last 7 days" />
        <StatCard label="Unverified" value={d.moderation.unverified_users} icon={EyeOff} explain="Accounts without a confirmed email." sub="pending verify" />
        <StatCard label="Banned" value={d.moderation.banned_users} icon={Ban} explain="Restricted accounts." sub="restricted" />
      </div>
      <TrendChart title="Signups & Uploads — last 14 days" data={growthSeries} testid="insights-growth-chart"
        series={[{ key: "signups", color: "#00E5FF", label: "Signups" }, { key: "uploads", color: "#0066FF", label: "Uploads" }]} unit="" line />

      <SectionHead title="Leaderboards" hint="your most-loved creators & posts" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="insights-top-creators">
          <span className="overline">Top Creators</span>
          <div className="mt-3 space-y-2.5">
            {d.top_creators.length === 0 && <p className="text-[#52525B] text-sm">No data yet.</p>}
            {d.top_creators.map((c, i) => (
              <div key={c.username} className="flex items-center gap-3">
                <span className="w-5 text-[#0066FF] font-heading text-sm">{i + 1}</span>
                <span className="flex-1 truncate text-white text-sm">@{c.username}</span>
                <span className="font-mono text-[11px] text-[#52525B]">{c.posts} posts · {c.likes.toLocaleString()} likes</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="insights-top-media">
          <span className="overline">Top Media</span>
          <div className="mt-3 space-y-2.5">
            {d.top_media.length === 0 && <p className="text-[#52525B] text-sm">No data yet.</p>}
            {d.top_media.map((m, i) => (
              <Link to={`/media/${m.id}`} key={m.id} className="flex items-center gap-3 group">
                <span className="w-5 text-[#0066FF] font-heading text-sm">{i + 1}</span>
                <span className="flex-1 truncate text-white text-sm group-hover:text-[#00E5FF] transition-colors">{m.title}</span>
                <span className="font-mono text-[11px] text-[#52525B]">{m.likes.toLocaleString()} likes · {m.views.toLocaleString()} views</span>
              </Link>
            ))}
          </div>
        </div>
      </div>

      <SectionHead title="Category Mix" hint="how the library breaks down by kink" />
      <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="insights-categories">
        {d.categories.length === 0 && <p className="text-[#52525B] text-sm">No categorized media yet.</p>}
        <div className="space-y-2.5">
          {d.categories.map((c) => (
            <div key={c.name} className="flex items-center gap-3">
              <span className="w-44 shrink-0 truncate text-[#D4D4D8] text-xs font-mono">{c.name}</span>
              <div className="flex-1 h-2.5 bg-[#141414] rounded-full overflow-hidden"><div className="h-full bg-[#A855F7] rounded-full transition-all duration-700" style={{ width: `${(c.count / maxCat) * 100}%`, boxShadow: "0 0 10px rgba(168,85,247,0.6)" }} /></div>
              <span className="w-10 text-right font-mono text-[11px] text-[#52525B]">{c.count}</span>
            </div>
          ))}
        </div>
      </div>

      <SectionHead title="Moderation Queue" hint="content & accounts that may need staff attention" />
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Hidden" value={d.moderation.hidden} icon={EyeOff} explain="Removed from public view." sub="not visible" />
        <StatCard label="Private Drafts" value={d.moderation.private_drafts} icon={Lock} explain="Unpublished, owner-only media." sub="unpublished" />
        <StatCard label="Locked Threads" value={d.moderation.locked_threads} icon={MessageCircle} explain="Posts with comments disabled." sub="comments off" />
        <StatCard label="Unverified" value={d.moderation.unverified_users} icon={AlertTriangle} explain="Accounts without a confirmed email." sub="pending" />
        <StatCard label="Banned" value={d.moderation.banned_users} icon={Ban} explain="Restricted accounts." sub="restricted" />
      </div>
    </div>
  );
}

function Content() {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [missing, setMissing] = useState(0);
  const [backfilling, setBackfilling] = useState(false);
  const [aiMissing, setAiMissing] = useState(0);
  const [aiTotal, setAiTotal] = useState(0);
  const [aiBackfilling, setAiBackfilling] = useState(false);
  const [genericTitles, setGenericTitles] = useState(0);
  const [titleBackfilling, setTitleBackfilling] = useState(false);
  const [jobProgress, setJobProgress] = useState({ thumbs: null, ai: null, titles: null }); // {total, processed, failed}
  const [overnight, setOvernight] = useState(false);
  const [skippedItems, setSkippedItems] = useState({}); // {kind: [{id,title,media_type}]}
  const [skipModal, setSkipModal] = useState(null); // kind whose skipped list is open

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/media", { params: { q, filter, limit: 200 } });
      setMedia(data);
    } finally { setLoading(false); }
  }, [q, filter]);

  const loadMissing = useCallback(() => {
    api.get("/admin/thumbnails-missing").then(({ data }) => setMissing(data.missing)).catch(() => {});
    api.get("/admin/ai-missing").then(({ data }) => { setAiMissing(data.missing); setAiTotal(data.total || 0); }).catch(() => {});
    api.get("/admin/generic-titles-count").then(({ data }) => setGenericTitles(data.missing)).catch(() => {});
  }, []);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { loadMissing(); }, [loadMissing]);

  // Poll the server-side job doc for accurate live progress (no guessing).
  // Tolerant of transient network/Cloudflare hiccups — the job keeps running on
  // the server regardless, so a failed poll just retries.
  const pollJob = (jobId, kind, setActive, label) => {
    let fails = 0;
    const iv = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/backfill-status/${jobId}`);
        fails = 0;
        setJobProgress((p) => ({ ...p, [kind]: { jobId, total: data.total, processed: data.processed, failed: data.failed, status: p[kind]?.status === "stopping" && data.status !== "stopped" ? "stopping" : data.status, retryRound: data.retry_round, retryPending: data.retry_pending, nextRetryAt: data.next_retry_at, reason: data.skipped?.[0]?.reason } }));
        if (data.skipped?.length) setSkippedItems((s) => ({ ...s, [kind]: data.skipped }));
        if (data.status === "done" || data.status === "stopped") {
          clearInterval(iv);
          setActive(false);
          loadMissing();
          load();
          const ok = data.processed - data.failed;
          if (data.status === "stopped") {
            setSkippedItems((s) => ({ ...s, [kind]: data.skipped || [] }));
            toast.info(`${label} stopped — ${ok} done`);
          } else if (data.failed > 0) {
            setSkippedItems((s) => ({ ...s, [kind]: data.skipped || [] }));
            toast.info(`${label}: ${ok} done · ${data.failed} skipped — click "View skipped" to see which.`);
          } else {
            setSkippedItems((s) => ({ ...s, [kind]: [] }));
            toast.success(`${label} complete ✓ — ${ok} processed`);
          }
          setTimeout(() => setJobProgress((p) => ({ ...p, [kind]: null })), 2500);
        }
      } catch {
        fails += 1;
        // don't give up on a brief hiccup — the job is still running server-side
        if (fails >= 8) { clearInterval(iv); setActive(false); }
      }
    }, 4000);
  };

  const SETTERS = { ai: setAiBackfilling, titles: setTitleBackfilling, thumbs: setBackfilling };
  const KIND_MAP = { ai: "ai", "ai-retag": "ai", titles: "titles", thumbnails: "thumbs" };
  const RESUME_LABELS = { ai: "AI tagging", titles: "AI names", thumbs: "Thumbnails" };
  useEffect(() => {
    api.get("/admin/backfill-active").then(({ data }) => {
      data.forEach((j) => {
        const k = KIND_MAP[j.kind];
        if (!k) return;
        SETTERS[k](true);
        setJobProgress((p) => ({ ...p, [k]: { jobId: j.job_id, total: 0, processed: 0, failed: 0 } }));
        pollJob(j.job_id, k, SETTERS[k], RESUME_LABELS[k]);
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopJob = async (kind) => {
    const id = jobProgress[kind]?.jobId;
    if (!id) return;
    try {
      await api.post(`/admin/backfill-stop/${id}`);
      setJobProgress((p) => ({ ...p, [kind]: { ...p[kind], status: "stopping" } }));
      toast.success("Stopping…");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const startJob = async (endpoint, kind, setActive, label, noneMsg, extra = {}) => {
    setActive(true);
    setSkippedItems((s) => ({ ...s, [kind]: [] }));
    setJobProgress((p) => ({ ...p, [kind]: { total: 0, processed: 0, failed: 0 } }));
    try {
      const params = new URLSearchParams();
      if (overnight) params.set("pace", "1");
      Object.entries(extra).forEach(([k, v]) => params.set(k, v));
      const qs = params.toString();
      const url = qs ? `${endpoint}?${qs}` : endpoint;
      const { data } = await api.post(url);
      if (data.queued === 0) {
        toast.info(noneMsg);
        setActive(false);
        setJobProgress((p) => ({ ...p, [kind]: null }));
        return;
      }
      toast.success(`${label}: processing ${data.queued} item(s) in the background${overnight ? " (overnight mode — slow & steady)" : ""}…`);
      setJobProgress((p) => ({ ...p, [kind]: { jobId: data.job_id, total: data.queued, processed: 0, failed: 0 } }));
      pollJob(data.job_id, kind, setActive, label);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setActive(false);
      setJobProgress((p) => ({ ...p, [kind]: null }));
    }
  };

  const backfill = () => startJob("/admin/backfill-thumbnails", "thumbs", setBackfilling, "Thumbnails", "All media already have thumbnails ✓");
  const backfillAi = () => startJob("/admin/backfill-ai", "ai", setAiBackfilling, "AI tagging", "Everything's already AI-analyzed ✓");
  const backfillAiAll = () => {
    if (!window.confirm(`Re-run AI tagging & descriptions on ALL ${aiTotal || ""} media with the improved kink vocabulary? This is resource-intensive and may take a while — turn on Overnight mode for very large libraries.`)) return;
    startJob("/admin/backfill-ai", "ai", setAiBackfilling, "AI re-tagging (all)", "No media to re-tag", { force: 1 });
  };
  const backfillTitles = () => startJob("/admin/backfill-titles", "titles", setTitleBackfilling, "AI names", "No generic filenames found — all titled ✓");

  const act = async (id, fn) => {
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const patch = (id, changes) => setMedia((m) => m.map((x) => (x.id === id ? { ...x, ...changes } : x)));

  const feature = (m) => act(m.id, async () => { await api.post(`/admin/media/${m.id}/feature`, { value: !m.featured }); patch(m.id, { featured: !m.featured }); toast.success(!m.featured ? "Featured" : "Unfeatured"); });
  const hide = (m) => act(m.id, async () => { await api.post(`/admin/media/${m.id}/hide`, { value: !m.hidden }); patch(m.id, { hidden: !m.hidden }); toast.success(!m.hidden ? "Hidden" : "Unhidden"); });
  const lock = (m) => act(m.id, async () => { await api.post(`/admin/media/${m.id}/lock-comments`, { value: !m.comments_locked }); patch(m.id, { comments_locked: !m.comments_locked }); toast.success(!m.comments_locked ? "Comments locked" : "Comments unlocked"); });
  const remove = (m) => { if (!window.confirm("Remove this content?")) return; act(m.id, async () => { await api.delete(`/media/${m.id}`); setMedia((x) => x.filter((i) => i.id !== m.id)); toast.success("Removed"); }); };

  const [purgingSkipped, setPurgingSkipped] = useState(false);
  const deleteAllSkipped = async (kind) => {
    const ids = (skippedItems[kind] || []).filter((it) => it.permanent || kind === "thumbs").map((it) => it.id);
    if (ids.length === 0) return toast.info("None of these are broken files — they'll be retried");
    if (!window.confirm(`Permanently remove all ${ids.length} skipped/corrupt file${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setPurgingSkipped(true);
    try {
      const { data } = await api.post("/admin/delete-media", { ids });
      const gone = new Set(ids);
      setMedia((x) => x.filter((i) => !gone.has(i.id)));
      setSkippedItems((s) => ({ ...s, [kind]: [] }));
      setSkipModal(null);
      toast.success(`Removed ${data.deleted} skipped file${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't remove files");
    } finally {
      setPurgingSkipped(false);
    }
  };

  const FILTERS = [["all", "All"], ["image", "Photos"], ["video", "Videos"], ["featured", "Featured"], ["hidden", "Hidden"]];
  const KIND_LABELS = { ai: "AI tags", titles: "AI names", thumbs: "Thumbnails" };

  return (
    <div>
      <BackfillProgress
        jobs={[
          { key: "ai", active: aiBackfilling, prog: jobProgress.ai, label: "AI tagging", color: "#7c3aed" },
          { key: "titles", active: titleBackfilling, prog: jobProgress.titles, label: "AI names", color: "#0ea5e9" },
          { key: "thumbs", active: backfilling, prog: jobProgress.thumbs, label: "Thumbnails", color: "#0066FF" },
        ].filter((j) => j.active)}
        onStop={stopJob}
      />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={15} />
          <input data-testid="admin-content-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title, author, tag…" className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm pl-9 pr-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl font-mono" />
        </div>
        <div className="flex gap-1 flex-wrap">
          {FILTERS.map(([f, l]) => (
            <button key={f} data-testid={`admin-filter-${f}`} onClick={() => setFilter(f)} className={`px-3 py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors ${filter === f ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          ))}
        </div>
        <label
          data-testid="overnight-toggle-label"
          title="Process slowly, one file at a time with pacing, to completely avoid Cloudflare/rate-limit throttling. Best for very large batches (leave it running)."
          className="flex items-center gap-2 px-3 py-2 border border-[#2A2A2E] text-[10px] font-mono uppercase tracking-wider text-[#A1A1AA] cursor-pointer select-none rounded-xl"
        >
          <Moon size={13} className={overnight ? "text-[#0ea5e9]" : "text-[#52525B]"} />
          Overnight
          <Switch data-testid="overnight-toggle" checked={overnight} onCheckedChange={setOvernight} />
        </label>
        <button
          data-testid="generate-thumbnails-btn"
          onClick={backfill}
          disabled={backfilling}
          title="Generate thumbnails for media missing one (e.g. older videos)"
          className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors disabled:opacity-50"
        >
          {backfilling ? <Loader2 size={13} className="animate-spin" /> : <ImageIcon size={13} />}
          {backfilling ? `Processing…${jobProgress.thumbs ? ` (${jobProgress.thumbs.processed}/${jobProgress.thumbs.total})` : ""}` : `Generate Thumbnails${missing > 0 ? ` (${missing})` : ""}`}
        </button>
        <button
          data-testid="generate-ai-btn"
          onClick={backfillAi}
          disabled={aiBackfilling}
          title="Run AI auto-tagging + categorization on media that hasn't been analyzed"
          className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#7c3aed] text-[#a78bfa] hover:bg-[#7c3aed] hover:text-white transition-colors disabled:opacity-50"
        >
          {aiBackfilling ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          {aiBackfilling ? `Analyzing…${jobProgress.ai ? ` (${jobProgress.ai.processed}/${jobProgress.ai.total})` : ""}` : `Generate AI Tags${aiMissing > 0 ? ` (${aiMissing})` : ""}`}
        </button>
        <button
          data-testid="retag-all-ai-btn"
          onClick={backfillAiAll}
          disabled={aiBackfilling}
          title="RE-RUN AI tagging + descriptions on the ENTIRE library with the improved kink/fetish vocabulary. Resource-intensive — use Overnight mode for large libraries."
          className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#c026d3] text-[#e879f9] hover:bg-[#c026d3] hover:text-white transition-colors disabled:opacity-50"
        >
          {aiBackfilling ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Re-tag All{aiTotal > 0 ? ` (${aiTotal})` : ""}
        </button>
        <button
          data-testid="generate-ai-names-btn"
          onClick={backfillTitles}
          disabled={titleBackfilling}
          title="Generate AI titles for files with generic / system-given names (IMG_1234, VID_0001, DSC…, 'Imported media', etc.)"
          className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#0ea5e9] text-[#7dd3fc] hover:bg-[#0ea5e9] hover:text-white transition-colors disabled:opacity-50"
        >
          {titleBackfilling ? <Loader2 size={13} className="animate-spin" /> : <Type size={13} />}
          {titleBackfilling ? `Naming…${jobProgress.titles ? ` (${jobProgress.titles.processed}/${jobProgress.titles.total})` : ""}` : `Generate AI Names${genericTitles > 0 ? ` (${genericTitles})` : ""}`}
        </button>
      </div>

      {["ai", "titles", "thumbs"].some((k) => skippedItems[k]?.length) && (
        <div className="flex flex-wrap items-center gap-3 mb-5 -mt-1" data-testid="skipped-bar">
          {["ai", "titles", "thumbs"].filter((k) => skippedItems[k]?.length).map((k) => (
            <button
              key={k}
              data-testid={`view-skipped-${k}`}
              onClick={() => setSkipModal(k)}
              className="flex items-center gap-2 px-3 py-2 border border-[#F1A817] text-[#F1A817] hover:bg-[#F1A817]/10 transition-colors text-[10px] font-mono uppercase tracking-wider"
            >
              <AlertTriangle size={13} /> {KIND_LABELS[k]}: {skippedItems[k].length} skipped — view
            </button>
          ))}
        </div>
      )}

      <Dialog open={!!skipModal} onOpenChange={(o) => !o && setSkipModal(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#1C1C1F] text-[#FFFFFF] max-w-lg" data-testid="skipped-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle size={18} className="text-[#F1A817]" /> Skipped files{skipModal ? ` — ${KIND_LABELS[skipModal]}` : ""}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[#A1A1AA] text-sm">
            "Retrying" items hit a temporary AI limit and are retried automatically. Only "Broken" items can be removed.
          </p>
          <div className="max-h-[52vh] overflow-y-auto custom-scrollbar divide-y divide-[#1C1C1F] -mx-1 px-1">
            {(skippedItems[skipModal] || []).map((it) => (
              <div key={it.id} className="flex items-center justify-between gap-3 py-2.5" data-testid={`skipped-item-${it.id}`}>
                <div className="min-w-0">
                  <p className="text-sm text-[#FFFFFF] truncate">{it.title || "Untitled"}</p>
                  <p className="text-[11px] text-[#52525B] font-mono truncate">{it.media_type} · {it.id}</p>
                  {it.reason && <p className="text-[11px] text-[#F1A817] truncate" title={it.reason}>{it.permanent ? "Broken" : `Retrying (${it.attempts || 1} tries)`} · {it.reason}</p>}
                </div>
                <Link to={`/media/${it.id}`} target="_blank" rel="noreferrer" className="shrink-0 flex items-center gap-1 text-[#4599FF] text-xs hover:underline">
                  <ExternalLink size={13} /> Open
                </Link>
              </div>
            ))}
          </div>
          {(skippedItems[skipModal] || []).length > 0 && (
            <div className="flex items-center justify-between gap-3 pt-3 border-t border-[#1C1C1F]">
              <span className="text-[11px] text-[#52525B]">{(skippedItems[skipModal] || []).length} file{(skippedItems[skipModal] || []).length === 1 ? "" : "s"} couldn't be processed</span>
              <button
                data-testid="delete-all-skipped-btn"
                onClick={() => deleteAllSkipped(skipModal)}
                disabled={purgingSkipped}
                className="flex items-center gap-2 bg-[#E60023] hover:bg-[#ff1a3d] disabled:opacity-50 text-white text-xs font-semibold px-4 py-2 rounded-md transition-colors"
              >
                {purgingSkipped ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />} Remove broken files
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0066FF]" size={28} /></div>
      ) : media.length === 0 ? (
        <p className="text-[#52525B] font-mono text-sm py-10 text-center">No media found.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="admin-media-grid">
          {media.map((m) => (
            <div key={m.id} className={`border bg-[#0A0A0A] ${m.hidden ? "border-[#553300] opacity-70" : m.featured ? "border-[#0066FF]" : "border-[#1C1C1F]"}`}>
              <Link to={`/media/${m.id}`} className="block relative">
                {m.media_type === "video" ? <video src={mediaSrc(m.url)} poster={m.thumb_url ? mediaSrc(m.thumb_url) : undefined} preload="none" className="w-full h-36 object-cover bg-[#0A0A0A]" muted /> : <img src={mediaSrc(m.thumb_url || m.url)} alt={m.title} className="w-full h-36 object-cover bg-[#0A0A0A]" />}
                <div className="absolute top-1 left-1 flex gap-1">
                  {m.featured && <span className="bg-[#0066FF] text-white p-1"><Star size={11} fill="white" /></span>}
                  {m.hidden && <span className="bg-[#553300] text-white p-1"><EyeOff size={11} /></span>}
                  {m.comments_locked && <span className="bg-black/70 text-white p-1"><Lock size={11} /></span>}
                </div>
                <div className="absolute top-1 right-1 flex gap-1">
                  {m.reports > 0 && <span data-testid={`report-badge-${m.id}`} className="bg-[#FF2A5F] text-white font-mono text-[9px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5" title={`${m.reports} reports`}><AlertTriangle size={9} /> {m.reports}</span>}
                  <span className="bg-black/70 text-white font-mono text-[8px] uppercase px-1.5 py-0.5 rounded-full">{m.media_type === "video" ? "VID" : "IMG"}</span>
                </div>
                <div className="absolute bottom-1 right-1 bg-black/70 text-[#A1A1AA] font-mono text-[8px] px-1.5 py-0.5 rounded">{fmtSize(m.size_bytes)}{m.width && m.height ? ` · ${m.width}×${m.height}` : ""}</div>
              </Link>
              <div className="p-2">
                <p className="text-white text-xs font-semibold line-clamp-1">{m.title}</p>
                <Link to={`/u/${m.author_username}`} className="font-mono text-[9px] text-[#52525B] hover:text-[#0066FF]">@{m.author_username}</Link>
                <div className="flex items-center gap-2 font-mono text-[9px] text-[#52525B] mt-1">
                  <span className="flex items-center gap-0.5"><Heart size={9} /> {m.likes_count}</span>
                  <span className="flex items-center gap-0.5"><MessageCircle size={9} /> {m.comments_count}</span>
                  <span className="flex items-center gap-0.5"><Eye size={9} /> {m.views}</span>
                  <span className="ml-auto">{fmtDate(m.created_at)}</span>
                </div>
                {m.category && <span className="inline-block mt-1.5 text-[8px] font-mono uppercase tracking-wider text-[#a78bfa] border border-[#a78bfa]/30 rounded-full px-1.5 py-0.5">{m.category}</span>}
                {m.tags && m.tags.length > 0 && (
                  <p className="font-mono text-[9px] text-[#52525B] mt-1 line-clamp-1" title={m.tags.join(", ")}>
                    {m.ai_analyzed ? "AI " : ""}{m.tags.slice(0, 4).map((t) => `#${t}`).join(" ")}{m.tags.length > 4 ? ` +${m.tags.length - 4}` : ""}
                  </p>
                )}
              </div>
              <div className="grid grid-cols-4 border-t border-[#1C1C1F]">
                <button data-testid={`admin-feature-${m.id}`} onClick={() => feature(m)} title="Feature" className={`flex items-center justify-center py-2 hover:bg-[#1C1C1F] transition-colors ${m.featured ? "text-[#0066FF]" : "text-[#52525B]"}`}><Star size={14} /></button>
                <button data-testid={`admin-hide-${m.id}`} onClick={() => hide(m)} title="Hide/Show" className={`flex items-center justify-center py-2 hover:bg-[#1C1C1F] transition-colors ${m.hidden ? "text-[#FFB300]" : "text-[#52525B]"}`}>{m.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
                <button data-testid={`admin-lock-${m.id}`} onClick={() => lock(m)} title="Lock comments" className={`flex items-center justify-center py-2 hover:bg-[#1C1C1F] transition-colors ${m.comments_locked ? "text-[#FFB300]" : "text-[#52525B]"}`}>{m.comments_locked ? <Lock size={14} /> : <LockOpen size={14} />}</button>
                <button data-testid={`admin-delete-${m.id}`} onClick={() => remove(m)} title="Delete" className="flex items-center justify-center py-2 text-[#52525B] hover:text-[#0066FF] hover:bg-[#1C1C1F] transition-colors"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtSize(b) {
  if (!b) return "—";
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / 1024 / 1024).toFixed(1)} MB`;
  return `${(b / 1024 / 1024 / 1024).toFixed(2)} GB`;
}

function Duplicates() {
  const [groups, setGroups] = useState([]);
  const [status, setStatus] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);

  const loadStatus = useCallback(() => api.get("/admin/duplicates/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  const loadGroups = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/admin/duplicates"); setGroups(data.groups); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStatus(); loadGroups(); }, [loadStatus, loadGroups]);

  const scan = async () => {
    setScanning(true);
    try {
      const { data } = await api.post("/admin/duplicates/scan");
      if (data.queued === 0) {
        toast.info("Everything's already fingerprinted — refreshing results.");
        await loadGroups(); await loadStatus();
        setScanning(false);
        return;
      }
      toast.success(`Fingerprinting ${data.queued} file(s)… this runs in the background.`);
      let polls = 0;
      const iv = setInterval(async () => {
        polls += 1;
        try {
          const { data: s } = await api.get("/admin/duplicates/status");
          setStatus(s);
          if (s.pending === 0 || polls > 60) {
            clearInterval(iv);
            await loadGroups();
            setScanning(false);
            toast.success("Duplicate scan complete.");
          }
        } catch { clearInterval(iv); setScanning(false); }
      }, 3000);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setScanning(false);
    }
  };

  const toggle = (id) => setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectDupes = () => {
    const ids = new Set();
    groups.forEach((g) => g.items.forEach((it) => { if (!it.is_original) ids.add(it.id); }));
    setSelected(ids);
  };
  const clearSel = () => setSelected(new Set());

  const deleteSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Permanently delete ${selected.size} selected file(s)? This cannot be undone.`)) return;
    setDeleting(true);
    const ids = [...selected];
    let ok = 0;
    for (const id of ids) {
      try { await api.delete(`/media/${id}`); ok += 1; } catch (e) { console.error(`bulk delete failed for ${id}`, e); }
    }
    // drop deleted items; keep only groups that still have >1
    setGroups((gs) => gs
      .map((g) => ({ ...g, items: g.items.filter((it) => !selected.has(it.id)) }))
      .filter((g) => g.items.length > 1));
    setSelected(new Set());
    setDeleting(false);
    loadStatus();
    toast.success(`Deleted ${ok} file(s).`);
  };

  const totalDupes = groups.reduce((n, g) => n + (g.count - 1), 0);

  return (
    <div data-testid="admin-duplicates">
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <button data-testid="dup-scan-btn" onClick={scan} disabled={scanning}
          className="flex items-center gap-2 px-4 py-2.5 font-heading uppercase tracking-widest text-[11px] border border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors disabled:opacity-50">
          {scanning ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
          {scanning ? "Scanning…" : "Scan for duplicates"}
        </button>
        {status && (
          <span className="font-mono text-[11px] text-[#A1A1AA]" data-testid="dup-status">
            {status.hashed}/{status.total} fingerprinted{status.pending > 0 ? ` · ${status.pending} pending` : ""}
          </span>
        )}
        <div className="flex-1" />
        {groups.length > 0 && (
          <>
            <button data-testid="dup-select-dupes" onClick={selectDupes} className="font-heading uppercase tracking-widest text-[10px] border border-[#1C1C1F] text-[#FFFFFF] hover:border-[#0066FF] hover:text-white transition-colors px-3 py-2">Select all duplicates (keep oldest)</button>
            <button data-testid="dup-clear" onClick={clearSel} className="font-heading uppercase tracking-widest text-[10px] border border-[#1C1C1F] text-[#A1A1AA] hover:text-white transition-colors px-3 py-2">Clear</button>
            <button data-testid="dup-delete-selected" onClick={deleteSelected} disabled={deleting || selected.size === 0}
              className="flex items-center gap-2 px-4 py-2 font-heading uppercase tracking-widest text-[10px] bg-[#0066FF] text-white hover:bg-[#0055CC] transition-colors disabled:opacity-40">
              {deleting ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Delete selected ({selected.size})
            </button>
          </>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0066FF]" size={28} /></div>
      ) : groups.length === 0 ? (
        <div className="border border-dashed border-[#1C1C1F] py-20 text-center" data-testid="dup-empty">
          <Files className="text-[#1C1C1F] mx-auto mb-3" size={40} />
          <p className="text-[#52525B] font-mono text-xs uppercase tracking-widest">No duplicates found{status && status.pending > 0 ? " yet — finish scanning" : ""}</p>
        </div>
      ) : (
        <>
          <p className="font-mono text-xs text-[#A1A1AA] mb-4" data-testid="dup-summary">{groups.length} duplicate group(s) · {totalDupes} redundant file(s) can be removed</p>
          <div className="space-y-4">
            {groups.map((g) => (
              <div key={g.hash} data-testid={`dup-group-${g.hash.slice(0, 8)}`} className="border border-[#1C1C1F] bg-[#0A0A0A] p-3">
                <p className="font-mono text-[10px] text-[#52525B] mb-2 uppercase tracking-wider">{g.count} identical files · {g.hash.slice(0, 12)}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {g.items.map((it) => {
                    const sel = selected.has(it.id);
                    return (
                      <div key={it.id} data-testid={`dup-item-${it.id}`} className={`relative border bg-black transition-all ${sel ? "border-[#0066FF] ring-1 ring-[#0066FF]" : it.is_original ? "border-[#2f7d32]" : "border-[#1C1C1F]"}`}>
                        <button onClick={() => toggle(it.id)} data-testid={`dup-check-${it.id}`} className="absolute top-2 left-2 z-10 text-white">
                          {sel ? <CheckSquare size={20} className="text-[#0066FF]" /> : <Square size={20} className="text-white/70" />}
                        </button>
                        {it.is_original && <span className="absolute top-2 right-2 z-10 bg-[#2f7d32] text-white px-1.5 py-0.5 font-mono text-[8px] uppercase tracking-wider">Oldest · keep</span>}
                        <Link to={`/media/${it.id}`} className="block">
                          <img src={mediaSrc(it.thumb_url || it.url)} alt={it.title} className="w-full h-32 object-cover bg-[#0A0A0A]" />
                        </Link>
                        <div className="p-2">
                          <p className="text-white text-xs font-semibold line-clamp-1">{it.title || "Untitled"}</p>
                          <p className="font-mono text-[9px] text-[#52525B]">@{it.author_username} · {fmtSize(it.size)} · {it.views} views</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Members({ isAdmin, currentUserId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/users", { params: { q, role: roleFilter || undefined, limit: 500 } });
      setUsers(data);
    } finally { setLoading(false); }
  }, [q, roleFilter]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const patch = (id, changes) => setUsers((u) => u.map((x) => (x.id === id ? { ...x, ...changes } : x)));
  const act = async (fn) => { try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  const toggleVerified = (u) => act(async () => { const { data } = await api.post(`/admin/users/${u.id}/verified`, { value: !u.verified_manual }); patch(u.id, { verified: data.verified, verified_manual: data.verified_manual }); toast.success(data.verified_manual ? `@${u.username} is now verified` : `Removed @${u.username}'s badge`); });
  const setRole = (u, role) => act(async () => { const { data } = await api.put(`/admin/users/${u.id}/role`, { role }); patch(u.id, { role: data.role, verified: data.verified }); toast.success(`@${u.username} is now ${role}`); });
  const ban = (u) => act(async () => { await api.post(`/admin/users/${u.id}/ban`, { value: !u.banned }); patch(u.id, { banned: !u.banned }); toast.success(!u.banned ? "Banned" : "Unbanned"); });
  const purge = (u) => { if (!window.confirm(`Remove ALL media uploaded by @${u.username}?`)) return; act(async () => { const { data } = await api.delete(`/admin/users/${u.id}/media`); patch(u.id, { media_count: 0 }); toast.success(`Removed ${data.removed} items`); }); };

  const ROLE_BADGE = { admin: "text-[#0066FF] border-[#0066FF]", moderator: "text-[#FFB300] border-[#FFB300]", user: "text-[#A1A1AA] border-[#2A2A2E]" };

  return (
    <div>
      {isAdmin && <VerificationQueue onDecided={(id, ok) => ok && patch(id, { verified: true, verified_manual: true })} />}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-[#52525B]" size={15} />
          <input data-testid="admin-members-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username or email…" className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm pl-9 pr-3 py-2 focus:outline-none focus:border-[#0066FF] rounded-xl font-mono" />
        </div>
        <div className="flex gap-1">
          {[["", "All"], ["user", "Users"], ["moderator", "Mods"], ["admin", "Admins"]].map(([r, l]) => (
            <button key={r} data-testid={`members-filter-${r || "all"}`} onClick={() => setRoleFilter(r)} className={`px-3 py-2 font-mono text-[10px] uppercase tracking-wider border transition-colors ${roleFilter === r ? "bg-white text-black border-white" : "border-[#1C1C1F] text-[#A1A1AA] hover:text-white"}`}>{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="animate-spin text-[#0066FF]" size={28} /></div>
      ) : (
        <div className="border border-[#1C1C1F]" data-testid="members-list">
          <div className="hidden sm:grid grid-cols-[2fr_1fr_auto_auto] gap-4 px-4 py-3 bg-[#0A0A0A] border-b border-[#1C1C1F] font-mono text-[9px] uppercase tracking-widest text-[#52525B]">
            <span>Member</span><span>Stats</span><span>Role</span><span>Actions</span>
          </div>
          {users.map((u) => (
            <div key={u.id} data-testid={`member-row-${u.username}`} className="grid grid-cols-1 sm:grid-cols-[2fr_1fr_auto_auto] gap-3 sm:gap-4 px-4 py-3 border-b border-[#1C1C1F] items-center hover:bg-[#0A0A0A] transition-colors">
              <div className="flex items-center gap-3 min-w-0">
                <div className={`w-9 h-9 flex items-center justify-center font-heading text-white text-sm shrink-0 ${u.banned ? "bg-[#553300]" : "bg-[#0066FF]"}`}>{u.username?.[0]?.toUpperCase()}</div>
                <div className="min-w-0">
                  <Link to={`/u/${u.username}`} className="text-white text-sm font-semibold hover:text-[#0066FF] truncate flex items-center gap-1.5">
                    {u.display_name}
                    <VerifiedBadge show={u.verified} size={13} testId={`member-verified-${u.username}`} />
                    {u.email_verified ? <ShieldCheck size={12} className="text-[#31A24C]" title="Email verified" /> : <AlertTriangle size={12} className="text-[#F1A817]" title="Email not verified" />}
                    {u.oauth && <span className="font-mono text-[8px] uppercase text-[#A855F7] border border-[#A855F7]/40 rounded px-1">{u.oauth}</span>}
                  </Link>
                  <p className="font-mono text-[10px] text-[#52525B] truncate">@{u.username} · {u.email}</p>
                </div>
              </div>
              <div className="font-mono text-[10px] text-[#A1A1AA] leading-relaxed">
                <div>{u.media_count} posts · {(u.likes_received || 0).toLocaleString()} likes · {(u.views_received || 0).toLocaleString()} views</div>
                <div className="text-[#52525B]">{u.followers} followers · {u.following || 0} following · {u.comments_count || 0} comments</div>
                <div className="text-[#52525B]">joined {fmtDate(u.created_at)} · active {fmtDate(u.last_active)}{u.banned && <span className="text-[#FF2A5F] ml-1">· BANNED</span>}</div>
              </div>
              <div><span className={`inline-block border px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider ${ROLE_BADGE[u.role]}`}>{u.role}</span></div>
              <div className="flex items-center gap-1 flex-wrap">
                {isAdmin ? (
                  <>
                    {u.role !== "admin" && <button data-testid={`promote-admin-${u.username}`} onClick={() => setRole(u, "admin")} title="Make admin" className="p-1.5 text-[#52525B] hover:text-[#0066FF] border border-[#1C1C1F] hover:border-[#0066FF] transition-colors"><Crown size={13} /></button>}
                    {u.role !== "moderator" && <button data-testid={`promote-mod-${u.username}`} onClick={() => setRole(u, "moderator")} title="Make moderator" className="p-1.5 text-[#52525B] hover:text-[#FFB300] border border-[#1C1C1F] hover:border-[#FFB300] transition-colors"><ShieldCheck size={13} /></button>}
                    {u.role !== "user" && u.id !== currentUserId && <button data-testid={`demote-${u.username}`} onClick={() => setRole(u, "user")} title="Demote to user" className="p-1.5 text-[#52525B] hover:text-white border border-[#1C1C1F] transition-colors"><UserCheck size={13} /></button>}
                    <button data-testid={`verify-toggle-${u.username}`} onClick={() => toggleVerified(u)} disabled={u.role !== "user" && !u.verified_manual} title={u.role !== "user" ? "Staff are verified automatically" : (u.verified_manual ? "Remove verified badge" : "Give verified badge")} className={`p-1.5 border border-[#1C1C1F] transition-colors disabled:opacity-40 ${u.verified_manual ? "text-[#0066FF] border-[#0066FF]/50" : "text-[#52525B] hover:text-[#0066FF] hover:border-[#0066FF]"}`}><BadgeCheck size={13} /></button>
                    <button data-testid={`purge-${u.username}`} onClick={() => purge(u)} title="Remove all their media" className="p-1.5 text-[#52525B] hover:text-[#FFB300] border border-[#1C1C1F] transition-colors"><Hammer size={13} /></button>
                    {u.id !== currentUserId && <button data-testid={`ban-${u.username}`} onClick={() => ban(u)} title={u.banned ? "Unban" : "Ban"} className={`p-1.5 border border-[#1C1C1F] transition-colors ${u.banned ? "text-[#00FF66] hover:text-[#00FF66]" : "text-[#52525B] hover:text-[#0066FF]"}`}>{u.banned ? <UserCheck size={13} /> : <UserX size={13} />}</button>}
                  </>
                ) : (
                  <span className="font-mono text-[9px] text-[#2A2A2E] uppercase">View only</span>
                )}
              </div>
            </div>
          ))}
          {users.length === 0 && <p className="text-[#52525B] font-mono text-sm py-10 text-center">No members found.</p>}
        </div>
      )}
    </div>
  );
}


function retryLabel(p) {
  if (p.status === "stopping") return "Stopping…";
  if (p.status !== "retrying") return null;
  const secs = p.nextRetryAt ? Math.max(0, Math.round((new Date(p.nextRetryAt) - Date.now()) / 1000)) : 0;
  const when = secs > 3600 ? `${Math.floor(secs / 3600)}h ${Math.round((secs % 3600) / 60)}m` : secs > 60 ? `${Math.round(secs / 60)}m` : `${secs}s`;
  return `Retry #${p.retryRound} of ${p.retryPending} in ${when}`;
}

function BackfillProgress({ jobs, onStop }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <div data-testid="backfill-progress-panel" className="fixed bottom-6 right-6 z-[80] w-[320px] max-w-[calc(100vw-2rem)] bg-[#0A0A0A] border border-[#1C1C1F] rounded-xl shadow-[0_12px_28px_rgba(0,0,0,0.5)] p-4 animate-in slide-in-from-bottom-4 fade-in duration-300">
      <div className="flex items-center gap-2 mb-3">
        <Loader2 size={15} className="animate-spin text-[#4599FF]" />
        <span className="text-[#FFFFFF] font-semibold text-sm">Processing in background…</span>
      </div>
      <div className="space-y-3">
        {jobs.map((j) => {
          const p = j.prog || { total: 0, processed: 0, failed: 0 };
          const total = Math.max(p.total || 0, 1);
          const done = Math.max(0, Math.min(total, p.processed || 0));
          const pct = Math.round((done / total) * 100);
          return (
            <div key={j.key} data-testid={`backfill-progress-${j.key}`}>
              <div className="flex items-center justify-between text-xs mb-1.5">
                <span className="text-[#FFFFFF] font-medium">{j.label}</span>
                <span className="text-[#A1A1AA] font-mono">
                  {p.processed || 0}/{p.total || 0} · {pct}%
                  {p.failed > 0 ? <span className="text-[#F1A817]"> · {p.failed} pending</span> : null}
                </span>
              </div>
              {retryLabel(p) && <p data-testid={`backfill-retry-${j.key}`} className="text-[11px] text-[#F1A817] mb-1.5">{retryLabel(p)}{p.reason ? ` · ${p.reason}` : ""}</p>}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 rounded-full bg-[#020202] overflow-hidden">
                  <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, background: j.color }} />
                </div>
                <button data-testid={`backfill-stop-${j.key}`} onClick={() => onStop(j.key)} disabled={!p.jobId || p.status === "stopping"} className="flex items-center gap-1 text-[11px] font-semibold text-[#FF6B6B] hover:text-white disabled:opacity-40"><StopCircle size={14} /> Stop</button>
              </div>
            </div>
          );
        })}
      </div>
      <p className="text-[#52525B] text-[11px] mt-3 leading-relaxed">You can leave this page — it keeps running on the server.</p>
    </div>
  );
}

function barColor(p) {
  if (p >= 90) return "#ff3b5c";
  if (p >= 70) return "#f0a020";
  return "#00E5FF";
}

function fmtUptime(s) {
  if (!s) return "—";
  const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function fmtDate(iso) {
  if (!iso) return "never";
  try {
    const d = new Date(iso);
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days === 0) return "today";
    if (days === 1) return "yesterday";
    if (days < 30) return `${days}d ago`;
    return d.toLocaleDateString([], { month: "short", day: "numeric", year: "numeric" });
  } catch { return "—"; }
}

function healthStatus(p) {
  const v = p || 0;
  if (v >= 90) return { word: "Critical", color: "#ff3b5c" };
  if (v >= 75) return { word: "High", color: "#F1A817" };
  if (v >= 50) return { word: "Busy", color: "#00E5FF" };
  return { word: "Healthy", color: "#31A24C" };
}

function SectionHead({ title, hint }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 pt-3">
      <h3 className="font-heading uppercase tracking-widest text-xs text-white">{title}</h3>
      {hint && <span className="text-[#52525B] text-[11px] font-mono">{hint}</span>}
    </div>
  );
}

function Gauge({ label, percent, sub, icon: Icon, explain }) {
  const p = Math.min(100, Math.max(0, percent || 0));
  const st = healthStatus(p);
  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid={`sys-gauge-${label.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2 text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">
          {Icon && <Icon size={15} style={{ color: st.color }} />} {label}
        </div>
        <span className="px-2 py-0.5 rounded-full text-[10px] font-heading uppercase tracking-wider" style={{ color: st.color, background: `${st.color}1A`, border: `1px solid ${st.color}55` }}>{st.word}</span>
      </div>
      {explain && <p className="text-[#52525B] text-[11px] mb-3 leading-snug">{explain}</p>}
      <div className="flex items-end justify-between mb-2">
        <span className="font-heading text-3xl text-white tabular-nums leading-none" style={{ textShadow: `0 0 14px ${st.color}55` }}>{p.toFixed(0)}<span className="text-base text-[#52525B]">%</span></span>
      </div>
      <div className="h-2.5 bg-[#141414] rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700" style={{ width: `${p}%`, background: st.color, boxShadow: `0 0 12px ${st.color}` }} />
      </div>
      {sub && <p className="text-[#52525B] text-[11px] mt-2.5 font-mono">{sub}</p>}
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, explain }) {
  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid={`sys-stat-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center gap-2 text-[#A1A1AA] text-xs font-heading uppercase tracking-widest mb-2">
        {Icon && <Icon size={15} className="text-[#0066FF]" />} {label}
      </div>
      <p className="font-heading text-2xl text-white leading-none">{value}</p>
      {sub && <p className="text-[#52525B] text-[11px] mt-2 font-mono">{sub}</p>}
      {explain && <p className="text-[#3f3f46] text-[11px] mt-1.5 leading-snug">{explain}</p>}
    </div>
  );
}

function AnnouncementEditor() {
  const [cfg, setCfg] = useState({ enabled: false, text: "", speed: 30, color: "#0066FF" });
  const [saving, setSaving] = useState(false);
  const PRESETS = ["#0066FF", "#00E5FF", "#E11D48", "#D4AF37", "#31A24C"];
  useEffect(() => { api.get("/announcement").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/admin/announcement", cfg); toast.success(cfg.enabled ? "Announcement is live" : "Announcement saved (hidden)"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save"); }
    finally { setSaving(false); }
  };
  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid="announcement-editor">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2"><Megaphone size={18} className="text-[#00E5FF]" />
          <span className="text-white font-heading uppercase tracking-widest text-xs">Announcement bar</span></div>
        <button data-testid="announce-toggle" onClick={() => setCfg((c) => ({ ...c, enabled: !c.enabled }))}
          className={`relative w-11 h-6 rounded-full transition-colors shrink-0 ${cfg.enabled ? "bg-[#31A24C]" : "bg-[#1C1C1F]"}`}>
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${cfg.enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>
      <p className="text-[#52525B] text-[11px] mb-3">A scrolling message pinned to the very top of every page. Toggle on to show it site-wide.</p>
      <input data-testid="announce-text" value={cfg.text} maxLength={400} onChange={(e) => setCfg((c) => ({ ...c, text: e.target.value }))}
        placeholder="e.g. New leather archive drop this weekend · 18+ only · be respectful"
        className="w-full bg-[#141414] border border-[#1C1C1F] text-white text-sm px-3 py-2.5 rounded-lg focus:outline-none focus:border-[#00E5FF] mb-3" />
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-[#52525B] text-[10px] font-mono uppercase">Color</span>
          {PRESETS.map((c) => (
            <button key={c} data-testid={`announce-color-${c}`} onClick={() => setCfg((p) => ({ ...p, color: c }))}
              className={`w-6 h-6 rounded-full border-2 transition-transform ${cfg.color === c ? "border-white scale-110" : "border-transparent"}`} style={{ background: c }} />
          ))}
        </div>
        <label className="flex items-center gap-2 text-[#52525B] text-[10px] font-mono uppercase">Speed
          <input data-testid="announce-speed" type="range" min={5} max={120} value={cfg.speed} onChange={(e) => setCfg((c) => ({ ...c, speed: Number(e.target.value) }))} className="w-28 accent-[#00E5FF]" />
          <span className="text-[#A1A1AA]">{cfg.speed}s</span>
        </label>
        <button data-testid="announce-save" onClick={save} disabled={saving}
          className="ml-auto inline-flex items-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg hover:bg-[#00c9e0] transition-colors disabled:opacity-40">
          {saving ? <Loader2 className="animate-spin" size={13} /> : <Check size={13} />} Save
        </button>
      </div>
      {cfg.text?.trim() && (
        <div className="mt-4 announce-bar relative overflow-hidden rounded-lg border border-white/10" style={{ background: `linear-gradient(90deg, ${cfg.color}1a, ${cfg.color}33, ${cfg.color}1a)` }}>
          <div className="overflow-hidden py-1.5">
            <div className="animate-marquee flex whitespace-nowrap" style={{ animationDuration: `${cfg.speed}s`, width: "max-content" }}>
              {Array(6).fill(cfg.text.trim()).map((t, i) => (
                <span key={i} className="mx-6 font-mono text-[12px] uppercase tracking-[0.18em] text-white/90">{t}</span>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SystemPanel() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState(false);
  const [hist, setHist] = useState([]); // rolling time-series for the charts
  const [clearing, setClearing] = useState(false);
  const netRef = useRef(null);
  const ioRef = useRef(null);
  const hostRef = useRef(null);

  const clearCache = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/clear-cache");
      toast.success(`Freed ${data.freed_mb} MB on ${data.host}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClearing(false); }
  };

  useEffect(() => {
    let stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/admin/system-stats");
        if (!stop) {
          if (hostRef.current && hostRef.current !== data.host) {
            netRef.current = null; ioRef.current = null; setHist([]);
          }
          hostRef.current = data.host;
          setS(data); setErr(false);
          const nowT = Date.now();
          // network throughput (MB/s) from cumulative counters
          let rate = 0;
          const nowNet = (data.network?.recv_mb || 0) + (data.network?.sent_mb || 0);
          if (netRef.current) {
            const dt = (nowT - netRef.current.t) / 1000;
            if (dt > 0) rate = Math.max(0, (nowNet - netRef.current.v) / dt);
          }
          netRef.current = { t: nowT, v: nowNet };
          // disk I/O throughput (MB/s)
          let rRate = 0, wRate = 0;
          const rd = data.disk_io?.read_mb || 0, wr = data.disk_io?.write_mb || 0;
          if (ioRef.current) {
            const dt = (nowT - ioRef.current.t) / 1000;
            if (dt > 0) { rRate = Math.max(0, (rd - ioRef.current.r) / dt); wRate = Math.max(0, (wr - ioRef.current.w) / dt); }
          }
          ioRef.current = { t: nowT, r: rd, w: wr };
          const label = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
          setHist((h) => [...h, {
            t: label,
            cpu: data.cpu?.percent ?? 0,
            mem: data.memory?.percent ?? 0,
            disk: data.disk?.percent ?? 0,
            rss: data.process?.rss_mb ?? 0,
            net: Number(rate.toFixed(2)),
            read: Number(rRate.toFixed(2)),
            write: Number(wRate.toFixed(2)),
          }].slice(-30));
        }
      } catch (e) {
        if (!stop) setErr(true);
      }
      if (!stop) setTimeout(tick, 4000);
    };
    tick();
    return () => { stop = true; };
  }, []);

  if (err && !s) return <div className="space-y-4" data-testid="admin-system-panel"><AnnouncementEditor /><p className="text-[#ff3b5c] text-sm" data-testid="sys-error">Couldn't load system stats.</p></div>;
  if (!s) return <div className="space-y-4" data-testid="admin-system-panel"><AnnouncementEditor /><div className="flex items-center gap-2 text-[#52525B] text-sm py-10"><Loader2 className="animate-spin" size={16} /> Reading VM resources…</div></div>;

  const overall = Math.max(s.cpu.percent || 0, s.memory.percent || 0, s.disk.percent || 0);
  const ost = healthStatus(overall);
  const verdict = overall >= 90 ? "Resources are nearly maxed out — the app may slow down. Consider scaling up or freeing space."
    : overall >= 75 ? "Running hot but stable. Keep an eye on the metric shown below in amber/red."
    : overall >= 50 ? "Working steadily with comfortable headroom."
    : "All systems healthy — plenty of headroom.";

  return (
    <div className="space-y-4" data-testid="admin-system-panel">
      <AnnouncementEditor />
      {/* At-a-glance health verdict */}
      <div className="rounded-2xl p-5 border flex items-start gap-4" style={{ borderColor: `${ost.color}44`, background: `linear-gradient(135deg, ${ost.color}12, transparent 70%)` }} data-testid="sys-health-banner">
        <span className="mt-0.5 w-3 h-3 rounded-full shrink-0 animate-pulse" style={{ background: ost.color, boxShadow: `0 0 10px ${ost.color}` }} />
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-heading uppercase tracking-widest text-sm" style={{ color: ost.color }}>{ost.word === "Healthy" ? "All Systems Healthy" : `System ${ost.word}`}</span>
            <span className="text-[#52525B] text-[11px] font-mono">· busiest resource at {overall.toFixed(0)}%</span>
          </div>
          <p className="text-[#A1A1AA] text-sm mt-1">{verdict}</p>
          <p className="text-[#52525B] text-[11px] font-mono mt-1.5 flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#00E5FF] animate-pulse" /> Live · server <span data-testid="system-host" className="text-[#00E5FF]">{s.host}</span> · up {fmtUptime(s.uptime_seconds)} · media cache {s.app_cache_mb ?? 0} MB
          </p>
          <button data-testid="clear-cache-btn" onClick={clearCache} disabled={clearing} className="mt-3 flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-white/10 text-white hover:border-[#0066FF] disabled:opacity-50">
            {clearing ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />} Clear local cache
          </button>
        </div>
      </div>

      {/* Live health */}
      <SectionHead title="Live Health" hint="what your server is doing right this second" />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Gauge label="CPU" percent={s.cpu.percent} icon={Cpu}
          explain="How hard the processor is working. High for long spells means jobs are queuing."
          sub={`${s.cpu.cores} vCPU${s.cpu.freq_mhz ? ` @ ${(s.cpu.freq_mhz/1000).toFixed(1)}GHz` : ""} · load ${s.cpu.load.join(" / ")}`} />
        <Gauge label="Memory" percent={s.memory.percent} icon={Server}
          explain="RAM in use. If this stays near 100%, the app can crash or slow down."
          sub={`${s.memory.used_gb} / ${s.memory.total_gb} GB · ${s.memory.available_gb} free${s.memory.cached_gb ? ` · ${s.memory.cached_gb} cached` : ""}`} />
        <Gauge label="Disk" percent={s.disk.percent} icon={HardDrive}
          explain="Storage used by all media & data. Free space up by compressing your library."
          sub={`${s.disk.used_gb} / ${s.disk.total_gb} GB used · ${s.disk.free_gb} GB free`} />
      </div>

      {/* Per-core CPU */}
      {s.cpu.per_core && s.cpu.per_core.length > 1 && (
        <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid="sys-per-core">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">Per-Core CPU</span>
            <span className="text-[#52525B] text-[11px] font-mono">load on each of the {s.cpu.per_core.length} virtual cores</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
            {s.cpu.per_core.map((c, i) => {
              const st = healthStatus(c);
              return (
                <div key={i} className="text-center">
                  <div className="h-16 w-full bg-[#141414] rounded-md flex items-end overflow-hidden">
                    <div className="w-full rounded-md transition-all duration-500" style={{ height: `${Math.max(3, c)}%`, background: st.color, boxShadow: `0 0 8px ${st.color}88` }} />
                  </div>
                  <p className="text-[10px] font-mono text-[#52525B] mt-1">core {i} · {c.toFixed(0)}%</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Trends */}
      <SectionHead title="Trends" hint="last ~2 minutes · each point is a 4-second sample" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <TrendChart title="CPU & Memory %" data={hist} testid="sys-chart-cpumem"
          series={[{ key: "cpu", color: "#00E5FF", label: "CPU %" }, { key: "mem", color: "#0066FF", label: "Mem %" }]}
          domain={[0, 100]} unit="%" />
        <TrendChart title="Network I/O" data={hist} testid="sys-chart-net"
          series={[{ key: "net", color: "#31A24C", label: "MB/s" }]} unit=" MB/s" />
        <TrendChart title="Disk I/O" data={hist} testid="sys-chart-diskio"
          series={[{ key: "read", color: "#F1A817", label: "Read" }, { key: "write", color: "#ff3b5c", label: "Write" }]}
          unit=" MB/s" line />
      </div>

      {/* Runtime & network */}
      <SectionHead title="Runtime & Network" hint="the app process and data moving in/out" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="App Memory" value={`${s.process.rss_mb} MB`} icon={Activity}
          explain="Memory this app itself is holding."
          sub={`${s.process.threads} threads · ${s.process.cpu_percent.toFixed(0)}% cpu`} />
        <StatCard label="App Connections" value={s.process.connections ?? 0} icon={Network}
          explain="Open network sockets the app is using."
          sub={`${s.process.open_files ?? 0} open files`} />
        <StatCard label="App Uptime" value={fmtUptime(s.process.uptime_seconds)} icon={Clock}
          explain="How long the backend has run since its last restart."
          sub={`VM up ${fmtUptime(s.uptime_seconds)}`} />
        <StatCard label="Swap" value={`${s.swap.percent}%`} icon={Layers}
          explain="Overflow memory on disk. Above 0% for long = low on RAM."
          sub={`${s.swap.used_gb ?? 0} / ${s.swap.total_gb} GB`} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Net Total" value={`${((s.network.recv_mb + s.network.sent_mb)/1024).toFixed(1)} GB`} icon={Network}
          explain="Lifetime data transferred by this VM."
          sub={`↓ ${(s.network.recv_mb/1024).toFixed(1)}GB · ↑ ${(s.network.sent_mb/1024).toFixed(1)}GB`} />
        <StatCard label="Net Packets" value={((s.network.packets_recv + s.network.packets_sent)/1e6).toFixed(1) + "M"} icon={Activity}
          explain="Total packets in + out."
          sub={`${s.network.errors || 0} errors · ${s.network.drops || 0} drops`} />
        <StatCard label="Disk Written" value={fmtBytes((s.disk_io?.write_mb || 0) * 1048576)} icon={HardDrive}
          explain="Lifetime bytes written to disk."
          sub={`${fmtBytes((s.disk_io?.read_mb || 0) * 1048576)} read`} />
        <StatCard label="Processes" value={s.cpu.processes || 0} icon={Server}
          explain="Total OS processes running on the VM."
          sub={`${s.process.threads} app threads`} />
      </div>

      {/* Platform data */}
      <SectionHead title="Platform Data" hint="what's stored in your library and database" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Media" value={s.db.media.toLocaleString()} icon={Film} explain="Active photos & videos." sub={`${(s.db.videos||0).toLocaleString()} vids · ${(s.db.images||0).toLocaleString()} pics`} />
        <StatCard label="Members" value={s.db.users.toLocaleString()} icon={Users} explain="Registered accounts." sub={`${(s.db.verified_users||0).toLocaleString()} email-verified`} />
        <StatCard label="Comments" value={(s.db.comments || 0).toLocaleString()} icon={MessageCircle} explain="Total comments posted." sub="all-time" />
        <StatCard label="Private Drafts" value={(s.db.private_drafts || 0).toLocaleString()} icon={Lock} explain="Unpublished, owner-only media." sub="not public" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="AI Generated" value={(s.db.ai_generated || 0).toLocaleString()} icon={Wand2} explain="Images made with Nano Banana." sub="in library" />
        <StatCard label="Import Jobs" value={(s.db.import_jobs_total || 0).toLocaleString()} icon={RefreshCw} explain="All URL/HTML import jobs ever run." sub={`${s.db.import_jobs_running} running now`} />
        {s.mongo && <StatCard label="Database Size" value={fmtBytes((s.mongo.data_mb || 0) * 1048576)} icon={Server} explain="Space your records take (not media files)." sub={`${s.mongo.objects.toLocaleString()} records · ${s.mongo.collections} collections`} />}
        {s.mongo && <StatCard label="DB On Disk" value={fmtBytes((s.mongo.storage_mb || 0) * 1048576)} icon={HardDrive} explain="Actual disk the database occupies (incl. indexes)." sub={`${fmtBytes((s.mongo.index_mb || 0) * 1048576)} indexes`} />}
      </div>

      {/* System info */}
      <SectionHead title="System Info" hint="the machine this runs on" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="Host" value={s.sysinfo?.hostname?.slice(0, 12) || "—"} icon={Server} explain="Machine hostname." sub={s.sysinfo?.arch} />
        <StatCard label="OS" value={(s.sysinfo?.os || "").split("-")[0] || "Linux"} icon={Activity} explain="Operating system kernel." sub={(s.sysinfo?.os || "").slice(0, 22)} />
        <StatCard label="Python" value={s.sysinfo?.python || "—"} icon={Layers} explain="Backend runtime version." sub="FastAPI runtime" />
        <StatCard label="Sessions" value={s.logged_users ?? 0} icon={Users} explain="OS user sessions." sub={`${s.temp_c ? s.temp_c + "°C" : "temp n/a"}${s.battery != null ? ` · 🔋${s.battery}%` : ""}`} />
      </div>

      {/* CPU time breakdown */}
      {s.cpu_times && Object.keys(s.cpu_times).length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="sys-cpu-times">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">CPU Time Breakdown</span>
            <span className="text-[#52525B] text-[11px] font-mono">where the processor spends its cycles</span>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden bg-[#141414] mb-3">
            {[["user", "#00E5FF"], ["system", "#0066FF"], ["iowait", "#F1A817"], ["idle", "#1C1C1F"]].map(([k, c]) => (
              (s.cpu_times[k] || 0) > 0 && <div key={k} style={{ width: `${s.cpu_times[k]}%`, background: c }} title={`${k} ${s.cpu_times[k]}%`} />
            ))}
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1 font-mono text-[11px] text-[#A1A1AA]">
            {Object.entries(s.cpu_times).map(([k, v]) => <span key={k}>{k} <span className="text-white">{v}%</span></span>)}
          </div>
        </div>
      )}

      {/* Storage partitions + Memory detail */}
      <SectionHead title="Storage & Memory Detail" hint="mounted volumes and RAM breakdown" />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard label="RAM Active" value={`${s.memory.active_gb ?? 0} GB`} icon={Server} explain="Recently-used memory." sub={`${s.memory.shared_gb ?? 0} GB shared`} />
        <StatCard label="RAM Cached" value={`${s.memory.cached_gb ?? 0} GB`} icon={Layers} explain="Disk cache held in RAM (reclaimable)." sub={`${s.memory.buffers_gb ?? 0} GB buffers`} />
        {(s.partitions || []).slice(0, 2).map((p) => (
          <StatCard key={p.mount} label={`Vol ${p.mount}`} value={`${p.percent}%`} icon={HardDrive} explain={`${p.fs || "disk"} mounted at ${p.mount}`} sub={`${p.used_gb} / ${p.total_gb} GB`} />
        ))}
      </div>

      {/* Top processes */}
      {s.top_processes && s.top_processes.length > 0 && (
        <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="sys-top-processes">
          <div className="flex items-baseline justify-between mb-3">
            <span className="text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">Top Processes</span>
            <span className="text-[#52525B] text-[11px] font-mono">heaviest memory users on the box</span>
          </div>
          <div className="space-y-2">
            {s.top_processes.map((p, i) => {
              const max = s.top_processes[0].rss_mb || 1;
              return (
                <div key={i} className="flex items-center gap-3">
                  <span className="w-32 shrink-0 font-mono text-[11px] text-[#D4D4D8] truncate">{p.name}</span>
                  <div className="flex-1 h-2 bg-[#141414] rounded-full overflow-hidden">
                    <div className="h-full bg-[#0066FF] rounded-full" style={{ width: `${Math.max(3, (p.rss_mb / max) * 100)}%` }} />
                  </div>
                  <span className="w-16 text-right font-mono text-[11px] text-[#52525B]">{p.rss_mb} MB</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* MongoDB server */}
      {s.mongo_server && (
        <>
          <SectionHead title="MongoDB Server" hint={`v${s.mongo_server.version || "?"} · up ${s.mongo_server.uptime_hours}h`} />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard label="Connections" value={s.mongo_server.conn_current} icon={Network} explain="Open DB connections right now." sub={`${(s.mongo_server.conn_available/1000).toFixed(0)}k available`} />
            <StatCard label="Queries" value={s.mongo_server.op_query.toLocaleString()} icon={Search} explain="Lifetime read operations." sub={`${s.mongo_server.op_insert.toLocaleString()} inserts`} />
            <StatCard label="Writes" value={(s.mongo_server.op_update + s.mongo_server.op_delete).toLocaleString()} icon={Pencil} explain="Updates + deletes lifetime." sub={`${s.mongo_server.op_update.toLocaleString()} upd · ${s.mongo_server.op_delete.toLocaleString()} del`} />
            <StatCard label="DB Traffic" value={`${(s.mongo_server.net_out_mb/1024).toFixed(1)} GB`} icon={Activity} explain="Data the DB has sent." sub={`${(s.mongo_server.net_in_mb/1024).toFixed(1)} GB in · ${s.mongo_server.resident_mb} MB RAM`} />
          </div>
        </>
      )}

      {/* Collections + content */}
      <SectionHead title="Collections & Content" hint="documents per collection and library totals" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {s.collections && s.collections.length > 0 && (
          <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5" data-testid="sys-collections">
            <span className="text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">Collections</span>
            <div className="mt-3 space-y-1.5">
              {s.collections.map((c) => (
                <div key={c.name} className="flex items-center justify-between font-mono text-[11px]">
                  <span className="text-[#D4D4D8]">{c.name}</span>
                  <span className="text-[#52525B]">{c.count.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>
        )}
        {s.content && (
          <div className="grid grid-cols-2 gap-4 content-start">
            <StatCard label="Media Storage" value={fmtBytes(s.content.media_bytes)} icon={HardDrive} explain="Total size of stored media files." sub="measured items" />
            <StatCard label="Total Likes" value={s.content.total_likes.toLocaleString()} icon={Activity} explain="Likes across all posts." sub={`${s.content.total_views.toLocaleString()} views`} />
            <StatCard label="Follows" value={s.content.total_follows.toLocaleString()} icon={Users} explain="Follow relationships." sub={`${s.content.total_saves.toLocaleString()} saves`} />
            <StatCard label="Top Category" value={s.content.top_categories?.[0]?.name?.split(" ")[0] || "—"} icon={Film} explain="Most-used category." sub={s.content.top_categories?.[0] ? `${s.content.top_categories[0].count} items` : "—"} />
          </div>
        )}
      </div>

      <CompressionCard />
      <R2MigrateCard />
    </div>
  );
}

function R2MigrateCard() {
  const [st, setSt] = useState(null);
  const [starting, setStarting] = useState(false);
  const [health, setHealth] = useState(null);
  const [scan, setScan] = useState(null);
  const [purge, setPurge] = useState(null);
  const load = useCallback(() => { api.get("/admin/migrate-r2/status").then(({ data }) => setSt(data)).catch(() => {}); }, []);
  const loadHealth = useCallback(() => { api.get("/admin/r2-stats").then(({ data }) => setHealth(data)).catch(() => {}); }, []);
  const loadScan = useCallback(() => { api.get("/admin/broken-file-scan/status").then(({ data }) => setScan(data)).catch(() => {}); }, []);
  const loadPurge = useCallback(() => { api.get("/admin/purge-old-store/status").then(({ data }) => setPurge(data)).catch(() => {}); }, []);
  useEffect(() => { load(); loadHealth(); loadScan(); loadPurge(); }, [load, loadHealth, loadScan, loadPurge]);
  useEffect(() => { if (!st?.running) { loadHealth(); return; } const iv = setInterval(load, 3000); return () => clearInterval(iv); }, [st?.running, load, loadHealth]);
  useEffect(() => { if (!scan?.running) return; const iv = setInterval(loadScan, 2000); return () => clearInterval(iv); }, [scan?.running, loadScan]);
  useEffect(() => { if (!purge?.running) return; const iv = setInterval(loadPurge, 2000); return () => clearInterval(iv); }, [purge?.running, loadPurge]);

  const start = async () => {
    if (!window.confirm("Copy all stored files from the old Emergent store into Cloudflare R2? Safe to re-run — it skips files already copied.")) return;
    setStarting(true);
    try { await api.post("/admin/migrate-r2"); toast.success("Migration started."); setTimeout(load, 1000); }
    catch { toast.error("Couldn't start migration."); } finally { setStarting(false); }
  };
  const startScan = async () => {
    try { await api.post("/admin/broken-file-scan"); toast.success("Scanning for broken files…"); setTimeout(loadScan, 800); }
    catch { toast.error("Couldn't start scan."); }
  };
  const startPurge = async () => {
    if (!window.confirm("PURGE the OLD Emergent-store copies? This only deletes files already safely in R2 (never orphans anything). Only do this once you've confirmed R2 is stable in production. Continue?")) return;
    try { await api.post("/admin/purge-old-store"); toast.success("Purging old-store copies…"); setTimeout(loadPurge, 800); }
    catch { toast.error("Couldn't start purge."); }
  };
  const pct = st && st.total ? Math.round(((st.migrated + st.skipped + st.failed) / st.total) * 100) : 0;

  return (
    <div className="bg-[#0A0A0A] border border-white/5 rounded-2xl p-5 space-y-5" data-testid="r2-migrate-card">
      <div>
        <span className="text-white font-heading uppercase tracking-widest text-xs flex items-center gap-2 mb-3"><HardDrive size={15} className="text-[#00E5FF]" /> Cloudflare R2 Storage Health</span>
        <div className="grid grid-cols-2 gap-4" data-testid="r2-health">
          <div className="bg-[#141414] rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525B]">Objects in bucket</p>
            <p className="font-heading text-2xl text-white">{health?.objects != null ? health.objects.toLocaleString() : "—"}</p>
          </div>
          <div className="bg-[#141414] rounded-xl p-4">
            <p className="font-mono text-[10px] uppercase tracking-wider text-[#52525B]">Total size</p>
            <p className="font-heading text-2xl text-white">{health?.bytes != null ? fmtBytes(health.bytes) : "—"}</p>
          </div>
        </div>
      </div>

      <div className="hairline" />

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-white font-heading uppercase tracking-widest text-xs flex items-center gap-2"><RefreshCw size={14} className="text-[#F1A817]" /> Migrate Old Store → R2</span>
          <button data-testid="r2-migrate-btn" onClick={start} disabled={starting || st?.running}
            className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#F1A817] text-[#F1A817] hover:bg-[#F1A817] hover:text-black transition-colors disabled:opacity-50 rounded-lg">
            {st?.running || starting ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}{st?.running ? "Migrating…" : "Run Migration"}
          </button>
        </div>
        {st && (st.total > 0 || st.running || st.done) && (
          <>
            <div className="h-2 bg-[#141414] rounded-full overflow-hidden my-2"><div className="h-full bg-[#F1A817] rounded-full transition-all duration-500" style={{ width: `${pct}%` }} /></div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[11px] text-[#A1A1AA]" data-testid="r2-migrate-status">
              <span>{pct}%</span><span className="text-[#31A24C]">migrated {st.migrated}</span><span className="text-[#52525B]">skipped {st.skipped}</span>
              {st.failed > 0 && <span className="text-[#FF2A5F]">failed {st.failed}</span>}<span className="text-[#52525B]">of {st.total}</span>
              {st.done && !st.running && <span className="text-[#31A24C]">· done</span>}
            </div>
          </>
        )}
      </div>

      <div className="hairline" />

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-white font-heading uppercase tracking-widest text-xs flex items-center gap-2"><AlertTriangle size={14} className="text-[#0066FF]" /> Broken-File Scan</span>
          <button data-testid="r2-scan-btn" onClick={startScan} disabled={scan?.running}
            className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#0066FF] text-[#0066FF] hover:bg-[#0066FF] hover:text-white transition-colors disabled:opacity-50 rounded-lg">
            {scan?.running ? <Loader2 size={13} className="animate-spin" /> : <Search size={13} />}{scan?.running ? "Scanning…" : "Scan Now"}
          </button>
        </div>
        <p className="text-[#52525B] text-[11px] mb-2">Checks every media file exists in R2 so nothing silently 404s.</p>
        {scan && (scan.done || scan.running) && (
          <div data-testid="r2-scan-status" className="font-mono text-[11px]">
            <span className="text-[#A1A1AA]">checked {scan.checked}/{scan.total} · </span>
            {scan.missing?.length ? <span className="text-[#FF2A5F]">{scan.missing.length} missing</span> : <span className="text-[#31A24C]">all files present</span>}
            {scan.missing?.slice(0, 8).map((m) => (
              <Link key={m.id} to={`/media/${m.id}`} className="block text-[#FF2A5F] hover:underline truncate mt-0.5">{m.title} — {m.storage_path}</Link>
            ))}
          </div>
        )}
      </div>

      <div className="hairline" />

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className="text-white font-heading uppercase tracking-widest text-xs flex items-center gap-2"><Trash2 size={14} className="text-[#FF2A5F]" /> Purge Old Store (danger)</span>
          <button data-testid="r2-purge-btn" onClick={startPurge} disabled={purge?.running}
            className="flex items-center gap-2 px-3 py-2 font-heading uppercase tracking-widest text-[10px] border border-[#FF2A5F] text-[#FF2A5F] hover:bg-[#FF2A5F] hover:text-white transition-colors disabled:opacity-50 rounded-lg">
            {purge?.running ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}{purge?.running ? "Purging…" : "Purge"}
          </button>
        </div>
        <p className="text-[#52525B] text-[11px] mb-2">Deletes old Emergent-store copies — only for files confirmed in R2. Do this only after R2 is stable in production.</p>
        {purge && (purge.done || purge.running) && (
          <div data-testid="r2-purge-status" className="font-mono text-[11px] text-[#A1A1AA]">
            deleted {purge.deleted} · kept {purge.kept} · of {purge.total}{purge.done && !purge.running && <span className="text-[#31A24C]"> · done</span>}
          </div>
        )}
      </div>
    </div>
  );
}

function fmtBytes(n) {
  if (!n || n < 1024) return `${n || 0} B`;
  const u = ["KB", "MB", "GB", "TB"];
  let v = n / 1024, i = 0;
  while (v >= 1024 && i < u.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(1)} ${u[i]}`;
}

function KsiPanel() {
  const [stats, setStats] = useState(null);
  const [review, setReview] = useState([]);
  const [suggestions, setSuggestions] = useState({ pending: [], approved: [] });
  const [detail, setDetail] = useState(null);
  const [running, setRunning] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [nc, setNc] = useState({ key: "", label: "", kind: "material", synonyms: "" });
  const [progress, setProgress] = useState(null);
  const progRef = useRef(null);

  const pollProgress = () => {
    clearInterval(progRef.current);
    progRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/ksi/progress");
        setProgress(data);
        if (!data.running) { clearInterval(progRef.current); load(); }
      } catch { clearInterval(progRef.current); }
    }, 3000);
  };
  useEffect(() => () => clearInterval(progRef.current), []);

  const runAll = async () => {
    try {
      await api.post("/admin/ksi/reindex?all=true&only_unindexed=true");
      toast.success("Indexing the entire library — this runs in the background.");
      setProgress({ running: true, processed: 0 });
      pollProgress();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start");
    }
  };
  const cancelRun = async () => {
    try {
      const { data } = await api.post("/admin/ksi/cancel");
      if (data.cleared) {
        toast.success("Cleared a stuck run — you can start again now.");
        setProgress((p) => ({ ...(p || {}), running: false }));
        clearInterval(progRef.current);
        load();
      } else {
        toast("Stopping after the current item…");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't cancel");
    }
  };

  const rescanAll = async () => {
    if (!window.confirm("Re-scan the ENTIRE library against the latest concepts? This re-runs AI vision on every item (uses your Gemini key) and lets older uploads pick up newly-added concepts.")) return;
    try {
      await api.post("/admin/ksi/reindex?all=true&only_unindexed=false");
      toast.success("Full re-scan started — older media will pick up new concepts.");
      setProgress({ running: true, processed: 0 });
      pollProgress();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start");
    }
  };

  const approveSuggestion = async (key) => {
    try {
      const { data } = await api.post(`/admin/ksi/suggestions/${key}/approve`);
      toast.success(`Added "${data.label}" — tagged ${data.media_updated} item${data.media_updated === 1 ? "" : "s"}`);
      loadSuggestions(); load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const rejectSuggestion = async (key) => {
    try {
      await api.post(`/admin/ksi/suggestions/${key}/reject`);
      toast("Suggestion dismissed");
      loadSuggestions();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const loadSuggestions = () => {
    api.get("/admin/ksi/suggestions").then(({ data }) => setSuggestions(data || { pending: [], approved: [] })).catch(() => {});
  };

  const acceptAllReview = async () => {
    if (!window.confirm("Accept ALL items in the review queue as correctly tagged? This clears the queue (you can still correct individual items later).")) return;
    try {
      const { data } = await api.post("/admin/ksi/review/accept-all");
      toast.success(`Accepted ${data.accepted} item${data.accepted === 1 ? "" : "s"} — review queue cleared.`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const purgeTags = async () => {
    if (!window.confirm("Permanently delete ALL legacy tags from every media item? KSI evidence already imported is kept. This cannot be undone.")) return;
    try {
      const { data } = await api.post("/admin/ksi/purge-tags");
      toast.success(`Cleared legacy tags from ${data.cleared} items.`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  const doSearch = async (e) => {
    e?.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post("/admin/ksi/search", { query: query.trim(), limit: 20 });
      setResults(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Search failed");
    } finally { setSearching(false); }
  };

  const correct = async (mid, key, action) => {
    try {
      await api.post(`/admin/ksi/content/${mid}/correct`, { concept_key: key, action });
      toast.success(action === "remove" ? "Marked incorrect" : "Confirmed");
      inspect(mid);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const addConcept = async (e) => {
    e.preventDefault();
    if (!nc.key.trim()) return toast.error("Concept key required");
    try {
      await api.post("/admin/ksi/ontology", {
        key: nc.key, label: nc.label || nc.key, kind: nc.kind,
        parents: nc.kind ? [nc.kind] : [], synonyms: nc.synonyms.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(`Concept "${nc.key}" saved`);
      setNc({ key: "", label: "", kind: "material", synonyms: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed");
    }
  };

  const load = () => {
    api.get("/admin/ksi/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/admin/ksi/review").then(({ data }) => setReview(data || [])).catch(() => {});
    api.get("/admin/ksi/progress").then(({ data }) => { setProgress(data); if (data?.running) pollProgress(); }).catch(() => {});
    loadSuggestions();
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  const run = async () => {
    setRunning(true);
    try {
      await api.post("/admin/ksi/reindex?limit=200&only_unindexed=true");
      toast.success("KSI indexing started (sample of up to 200). Refresh stats in a bit.");
      setTimeout(load, 8000);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to start");
    } finally { setRunning(false); }
  };

  const inspect = async (mid) => {
    setDetail({ loading: true });
    try {
      const { data } = await api.get(`/admin/ksi/content/${mid}`);
      setDetail(data);
    } catch (e) {
      setDetail(null);
      toast.error(formatApiError(e.response?.data?.detail) || "Not indexed");
    }
  };

  const stateColor = { CONFIRMED: "#31A24C", PROBABLE: "#00E5FF", POSSIBLE: "#F1A817", UNKNOWN: "#8A8D91", CONTRADICTED: "#E60023" };
  const S = ({ label, value, color }) => (
    <div className="bg-[#141414] border border-[#1C1C1F] rounded-xl px-4 py-3">
      <p className="text-white font-heading text-2xl leading-none" style={{ color: color || "#fff" }}>{value}</p>
      <p className="text-[#52525B] text-[11px] font-mono uppercase tracking-wide mt-1">{label}</p>
    </div>
  );

  return (
    <div className="max-w-[1000px]" data-testid="ksi-panel">
      <div className="flex items-center gap-3 mb-2">
        <Network className="text-[#00E5FF]" size={24} />
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white">KinkVault Semantic Index</h2>
        <span className="text-[10px] font-mono uppercase tracking-widest text-[#00E5FF] bg-[#00E5FF]/10 border border-[#00E5FF]/25 px-2 py-0.5 rounded-full">phase 4 · learning</span>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-5">Evidence-weighted concepts with confidence, roles and contradictions — every tag is explainable ("Why was this tagged?"). The index also <span className="text-white">discovers new concepts</span> it sees in your media: frequent, confident ones are added automatically, the rest wait for your approval below.</p>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-5" data-testid="ksi-stats">
          <S label="Media total" value={stats.media_total} />
          <S label="KSI indexed" value={stats.ksi_indexed} color="#31A24C" />
          <S label="Needs review" value={stats.ksi_needs_review} color="#F1A817" />
          <S label="Ontology concepts" value={stats.ontology_concepts} color="#00E5FF" />
          <S label="New suggestions" value={stats.pending_suggestions ?? 0} color="#A855F7" />
        </div>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button data-testid="ksi-run-btn" onClick={run} disabled={running || progress?.running}
          className="flex items-center gap-2 bg-[#141414] border border-[#00E5FF]/40 text-[#00E5FF] font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl hover:bg-[#00E5FF]/10 transition-colors disabled:opacity-50">
          {running ? <Loader2 size={16} className="animate-spin" /> : <Network size={16} />} Index sample (200)
        </button>
        <button data-testid="ksi-run-all-btn" onClick={runAll} disabled={progress?.running}
          className="flex items-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl hover:bg-[#00c9e0] transition-colors disabled:opacity-50">
          <Network size={16} /> Index new items
        </button>
        <button data-testid="ksi-rescan-btn" onClick={rescanAll} disabled={progress?.running}
          className="flex items-center gap-2 bg-[#141414] border border-[#A855F7]/40 text-[#A855F7] font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl hover:bg-[#A855F7]/10 transition-colors disabled:opacity-50">
          <Network size={16} /> Re-scan for new concepts
        </button>
        <button data-testid="ksi-purge-tags-btn" onClick={purgeTags}
          className="flex items-center gap-2 text-[#E60023] border border-[#E60023]/40 px-4 py-3 rounded-xl text-xs font-semibold hover:bg-[#E60023]/10">
          <Trash2 size={15} /> Purge legacy tags
        </button>
        {progress?.running && (
          <button data-testid="ksi-cancel-btn" onClick={cancelRun} className="flex items-center gap-2 text-[#E60023] border border-[#E60023]/40 px-4 py-3 rounded-xl text-xs font-semibold hover:bg-[#E60023]/10">
            <StopCircle size={15} /> Stop
          </button>
        )}
        {progress && (
          <span className="text-[#8A8D91] font-mono text-xs" data-testid="ksi-progress">
            {progress.running ? "indexing… " : "last run: "}{progress.processed || 0}{progress.target ? `/${progress.target}` : ""} · ✔{progress.indexed || 0} · ⚑{progress.review || 0} · ✖{progress.failed || 0}
          </span>
        )}
      </div>

      <form onSubmit={doSearch} className="flex gap-2 mb-6" data-testid="ksi-search-form">
        <input data-testid="ksi-search-input" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Shadow semantic search — e.g. 'rubber boots in an industrial setting'"
          className="flex-1 bg-[#141414] border border-[#1C1C1F] text-white px-4 py-2.5 rounded-xl text-sm focus:outline-none focus:border-[#00E5FF] placeholder:text-[#52525B]" />
        <button type="submit" disabled={searching} className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-[#00E5FF]/10 text-sm font-semibold disabled:opacity-50">
          {searching ? <Loader2 size={15} className="animate-spin" /> : <BarChart3 size={15} />} Search
        </button>
      </form>
      {results && (
        <div className="mb-6 bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-4" data-testid="ksi-search-results">
          <p className="text-[#52525B] text-[11px] font-mono mb-2">expanded concepts: {results.expanded_concepts.join(", ") || "none"} · {results.count} results (shadow, not production)</p>
          {results.results.map((r) => (
            <div key={r.media_id} className="flex items-center gap-2 py-1.5 border-b border-white/[0.04] last:border-0">
              <span className="text-[#00E5FF] font-mono text-xs w-12">{r.score}</span>
              <span className="text-white text-sm truncate flex-1">{r.title || "Untitled"}</span>
              <span className="text-[#52525B] text-[10px] font-mono">{r.matched_concepts.map((c) => c.key).join(",")}</span>
            </div>
          ))}
          {results.count === 0 && <p className="text-[#52525B] text-xs">No indexed matches yet — index more items first.</p>}
        </div>
      )}

      <div className="mb-6 bg-[#0A0A0A] border border-[#A855F7]/25 rounded-2xl p-4" data-testid="ksi-suggestions">
        <h3 className="text-white font-heading uppercase tracking-tight text-sm mb-1 flex items-center gap-2">
          <Sparkles size={15} className="text-[#A855F7]" /> Discovered concepts ({suggestions.pending.length})
        </h3>
        <p className="text-[#52525B] text-[11px] font-mono mb-3">Concepts the AI spotted that aren't in the ontology yet. Approve to add them (and back-tag every item they were seen on) or dismiss.</p>
        {suggestions.pending.length === 0 ? (
          <p className="text-[#52525B] text-xs font-mono" data-testid="ksi-suggestions-empty">Nothing new discovered yet — index or re-scan media and fresh concepts will surface here.</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-[360px] overflow-y-auto">
            {suggestions.pending.map((s) => (
              <div key={s.key} data-testid={`ksi-suggestion-${s.key}`} className="flex items-center gap-3 bg-[#141414] border border-[#1C1C1F] rounded-xl px-3 py-2">
                {s.thumb_url ? (
                  <img src={mediaSrc(s.thumb_url)} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0 bg-black" />
                ) : (
                  <span className="w-10 h-10 rounded-lg bg-[#A855F7]/15 border border-[#A855F7]/30 flex items-center justify-center shrink-0"><Sparkles size={16} className="text-[#A855F7]" /></span>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-semibold truncate">{s.label} <span className="text-[#52525B] font-mono text-[10px] uppercase">· {s.kind}</span></p>
                  <p className="text-[#8A8D91] text-[11px] font-mono">seen on {s.count} item{s.count === 1 ? "" : "s"} · peak {Math.round((s.max_confidence || 0) * 100)}%</p>
                </div>
                <button data-testid={`ksi-suggestion-approve-${s.key}`} onClick={() => approveSuggestion(s.key)} title="Add to ontology" className="flex items-center gap-1 text-[#31A24C] border border-[#31A24C]/40 hover:bg-[#31A24C]/15 rounded-lg px-2.5 py-1.5 text-xs font-semibold"><Check size={13} /> Add</button>
                <button data-testid={`ksi-suggestion-reject-${s.key}`} onClick={() => rejectSuggestion(s.key)} title="Dismiss" className="text-[#E60023] hover:bg-[#E60023]/15 rounded-lg p-1.5"><X size={14} /></button>
              </div>
            ))}
          </div>
        )}
        {suggestions.approved.length > 0 && (
          <div className="mt-3 pt-3 border-t border-white/[0.06]">
            <p className="text-[#52525B] text-[11px] font-mono mb-2">Recently auto-added:</p>
            <div className="flex flex-wrap gap-1.5">
              {suggestions.approved.map((s) => (
                <span key={s.key} className="text-[11px] text-[#31A24C] bg-[#31A24C]/10 border border-[#31A24C]/25 px-2 py-0.5 rounded-full">{s.label}{s.approved_by === "auto" ? " ·auto" : ""}</span>
              ))}
            </div>
          </div>
        )}
      </div>

      <form onSubmit={addConcept} className="flex flex-wrap gap-2 mb-6 items-end bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-4" data-testid="ksi-ontology-form">
        <div className="flex flex-col"><label className="text-[#52525B] text-[10px] font-mono uppercase mb-1">Concept key</label>
          <input data-testid="ksi-oc-key" value={nc.key} onChange={(e) => setNc({ ...nc, key: e.target.value })} placeholder="neoprene" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm w-32 focus:outline-none focus:border-[#00E5FF]" /></div>
        <div className="flex flex-col"><label className="text-[#52525B] text-[10px] font-mono uppercase mb-1">Label</label>
          <input value={nc.label} onChange={(e) => setNc({ ...nc, label: e.target.value })} placeholder="Neoprene" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm w-32 focus:outline-none focus:border-[#00E5FF]" /></div>
        <div className="flex flex-col"><label className="text-[#52525B] text-[10px] font-mono uppercase mb-1">Kind</label>
          <input value={nc.kind} onChange={(e) => setNc({ ...nc, kind: e.target.value })} placeholder="material" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm w-28 focus:outline-none focus:border-[#00E5FF]" /></div>
        <div className="flex flex-col flex-1 min-w-[160px]"><label className="text-[#52525B] text-[10px] font-mono uppercase mb-1">Synonyms (comma)</label>
          <input value={nc.synonyms} onChange={(e) => setNc({ ...nc, synonyms: e.target.value })} placeholder="wetsuit, scuba" className="bg-[#141414] border border-[#1C1C1F] text-white px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-[#00E5FF]" /></div>
        <button type="submit" data-testid="ksi-oc-add" className="px-4 py-2 rounded-lg bg-[#00E5FF] text-black text-sm font-semibold hover:bg-[#00c9e0]">Add concept</button>
      </form>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <h3 className="text-white font-heading uppercase tracking-tight text-sm mb-3 flex items-center gap-2 justify-between">
            <span className="flex items-center gap-2"><AlertTriangle size={15} className="text-[#F1A817]" /> Review queue ({stats?.ksi_needs_review ?? review.length})</span>
            {(stats?.ksi_needs_review || review.length) > 0 && (
              <button data-testid="ksi-accept-all-btn" onClick={acceptAllReview} className="flex items-center gap-1.5 text-[#31A24C] border border-[#31A24C]/40 hover:bg-[#31A24C]/15 rounded-lg px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"><Check size={12} /> Accept all</button>
            )}
          </h3>
          <div className="flex flex-col gap-2 max-h-[420px] overflow-y-auto" data-testid="ksi-review-list">
            {review.length === 0 && <p className="text-[#52525B] text-xs font-mono">Nothing queued.</p>}
            {review.map((r) => (
              <button key={r.media_id} data-testid={`ksi-review-${r.media_id}`} onClick={() => inspect(r.media_id)}
                className="text-left bg-[#141414] border border-[#1C1C1F] rounded-xl px-4 py-3 hover:border-[#00E5FF] transition-colors">
                <p className="text-white text-sm truncate">{r.title || "Untitled"} <span className="text-[#52525B] font-mono text-[10px]">({r.media_type})</span></p>
                {(r.reasons || []).map((x, i) => <p key={i} className="text-[#F1A817] text-[11px]">• {x}</p>)}
              </button>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-white font-heading uppercase tracking-tight text-sm mb-3 flex items-center gap-2"><BarChart3 size={15} className="text-[#00E5FF]" /> Why was this tagged?</h3>
          {!detail && <p className="text-[#52525B] text-xs font-mono">Select a review item to inspect its evidence.</p>}
          {detail?.loading && <Loader2 className="animate-spin text-[#00E5FF]" size={20} />}
          {detail && !detail.loading && (
            <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-4" data-testid="ksi-detail">
              <p className="text-white text-sm font-semibold mb-1">{detail.media?.title}</p>
              <p className="text-[#52525B] text-[11px] font-mono mb-3">status: {detail.content?.status} · flat tags: {(detail.media?.existing_tags || []).slice(0, 6).join(", ")}</p>
              <div className="flex flex-col gap-2">
                {detail.concepts.map((c) => (
                  <div key={c.concept_key} className="bg-[#141414] border border-[#1C1C1F] rounded-lg px-3 py-2">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-white text-sm">{c.concept_key} <span className="text-[#52525B] text-[10px]">· {c.role}</span></span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-[11px] px-2 py-0.5 rounded-full" style={{ color: stateColor[c.state], background: `${stateColor[c.state]}18` }}>{c.state} {Math.round(c.confidence * 100)}%</span>
                        <button title="Confirm correct" data-testid={`ksi-confirm-${c.concept_key}`} onClick={() => correct(detail.content.media_id, c.concept_key, "confirm")} className="text-[#31A24C] hover:bg-[#31A24C]/15 rounded p-1"><Check size={14} /></button>
                        <button title="Mark incorrect" data-testid={`ksi-remove-${c.concept_key}`} onClick={() => correct(detail.content.media_id, c.concept_key, "remove")} className="text-[#E60023] hover:bg-[#E60023]/15 rounded p-1"><X size={14} /></button>
                      </div>
                    </div>
                    <p className="text-[#8A8D91] text-[11px] mt-1">support {c.breakdown?.support} · contra {c.breakdown?.contradiction} · {c.breakdown?.agreement}{c.human_verified ? " · ✔ human" : ""}</p>
                  </div>
                ))}
                {detail.concepts.length === 0 && <p className="text-[#52525B] text-xs">No concepts reached threshold — model was uncertain.</p>}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SafetyPanel() {
  const [progress, setProgress] = useState(null);
  const [flagged, setFlagged] = useState({ total: 0, items: [] });
  const [busy, setBusy] = useState(false);
  const progRef = useRef(null);

  const loadFlagged = () => api.get("/admin/safety/flagged", { params: { limit: 200 } }).then(({ data }) => setFlagged(data || { total: 0, items: [] })).catch(() => {});
  const loadProgress = () => api.get("/admin/safety/progress").then(({ data }) => { setProgress(data); if (data?.running) poll(); }).catch(() => {});

  const poll = () => {
    clearInterval(progRef.current);
    progRef.current = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/safety/progress");
        setProgress(data);
        if (!data.running) { clearInterval(progRef.current); loadFlagged(); }
      } catch { clearInterval(progRef.current); }
    }, 3000);
  };
  useEffect(() => { loadProgress(); loadFlagged(); return () => clearInterval(progRef.current); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startScan = async (all) => {
    if (!window.confirm(all ? "Scan the ENTIRE library for minors & women? This uses your Gemini key and may take a while." : "Scan up to 500 not-yet-scanned items now?")) return;
    setBusy(true);
    try {
      await api.post(`/admin/safety/scan?${all ? "all=true&only_unscanned=false" : "limit=500&only_unscanned=true"}`);
      toast.success("Safety scan started — flagged media is hidden immediately as it's found.");
      setProgress({ running: true, processed: 0 });
      poll();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't start scan");
    } finally { setBusy(false); }
  };

  const cancel = async () => {
    try {
      const { data } = await api.post("/admin/safety/cancel");
      toast(data.cleared ? "Cleared a stuck scan." : "Stopping after current item…");
      if (data.cleared) { setProgress((p) => ({ ...(p || {}), running: false })); clearInterval(progRef.current); }
    } catch {}
  };

  const restore = async (id) => {
    try { await api.post(`/admin/safety/${id}/restore`); toast.success("Restored — back on the site"); setFlagged((f) => ({ total: f.total - 1, items: f.items.filter((i) => i.id !== id) })); }
    catch { toast.error("Couldn't restore"); }
  };
  const purge = async (id) => {
    if (!window.confirm("Permanently delete this item and its file? This cannot be undone.")) return;
    try { await api.post(`/admin/safety/${id}/purge`); toast.success("Purged permanently"); setFlagged((f) => ({ total: f.total - 1, items: f.items.filter((i) => i.id !== id) })); }
    catch { toast.error("Couldn't purge"); }
  };
  const purgeAll = async () => {
    if (!window.confirm(`Permanently delete ALL ${flagged.total} flagged items and their files? This cannot be undone.`)) return;
    try { const { data } = await api.post("/admin/safety/purge-all"); toast.success(`Purged ${data.purged} items`); loadFlagged(); }
    catch { toast.error("Couldn't purge all"); }
  };

  const running = progress?.running;
  return (
    <div className="max-w-[1000px]" data-testid="safety-panel">
      <div className="flex items-center gap-3 mb-2">
        <ShieldCheck className="text-[#E60023]" size={24} />
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white">Content safety scan</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-5">Scans every photo &amp; video with AI and <span className="text-white">instantly hides</span> anything that appears to contain a <span className="text-[#E60023]">minor</span> or a <span className="text-[#E60023]">woman</span>. Removals are recoverable — review below and restore false positives or purge for good.</p>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <button data-testid="safety-scan-btn" onClick={() => startScan(false)} disabled={busy || running}
          className="flex items-center gap-2 bg-[#141414] border border-[#E60023]/40 text-[#ff6b81] font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl hover:bg-[#E60023]/10 transition-colors disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Scan next 500
        </button>
        <button data-testid="safety-scan-all-btn" onClick={() => startScan(true)} disabled={busy || running}
          className="flex items-center gap-2 bg-[#E60023] text-white font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl hover:bg-[#c4001d] transition-colors disabled:opacity-50">
          <ShieldCheck size={16} /> Scan entire library
        </button>
        {running && (
          <button data-testid="safety-cancel-btn" onClick={cancel} className="flex items-center gap-2 text-[#E60023] border border-[#E60023]/40 px-4 py-3 rounded-xl text-xs font-semibold hover:bg-[#E60023]/10">
            <StopCircle size={15} /> Stop
          </button>
        )}
        {progress && (
          <span className="text-[#8A8D91] font-mono text-xs" data-testid="safety-progress">
            {running ? "scanning… " : "last: "}{progress.processed || 0}{progress.target ? `/${progress.target}` : ""} · flagged {progress.flagged || 0} (⚑{progress.minors || 0} minors · {progress.women || 0} women){progress.failed ? ` · ${progress.failed} unreadable` : ""}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between mb-3">
        <h3 className="text-white font-heading uppercase tracking-tight text-sm flex items-center gap-2"><EyeOff size={15} className="text-[#E60023]" /> Removed &amp; hidden ({flagged.total})</h3>
        {flagged.total > 0 && (
          <button data-testid="safety-purge-all-btn" onClick={purgeAll} className="flex items-center gap-1.5 text-[#E60023] border border-[#E60023]/40 hover:bg-[#E60023]/15 rounded-lg px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider"><Trash2 size={13} /> Purge all permanently</button>
        )}
      </div>

      {flagged.items.length === 0 ? (
        <p className="text-[#52525B] text-xs font-mono" data-testid="safety-flagged-empty">Nothing flagged. Run a scan above.</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="safety-flagged-grid">
          {flagged.items.map((it) => (
            <div key={it.id} data-testid={`safety-flagged-${it.id}`} className="border border-[#1C1C1F] bg-black rounded-lg overflow-hidden">
              <div className="aspect-[3/4] bg-[#0A0A0A] flex items-center justify-center overflow-hidden">
                {it.thumb_url ? <img src={mediaSrc(it.thumb_url)} alt="" className="w-full h-full object-cover" /> : <Film size={28} className="text-[#2A2A2E]" />}
              </div>
              <div className="p-2">
                <span className={`inline-block text-[9px] font-mono uppercase px-1.5 py-0.5 rounded-full ${it.reason === "minor" ? "bg-[#E60023]/20 text-[#ff6b81]" : "bg-[#F1A817]/20 text-[#F1A817]"}`}>{it.reason} {Math.round((it.confidence || 0) * 100)}%</span>
                <p className="text-white text-[11px] mt-1 truncate" title={it.title}>{it.title || "Untitled"}</p>
                <div className="flex gap-1.5 mt-2">
                  <button data-testid={`safety-restore-${it.id}`} onClick={() => restore(it.id)} className="flex-1 flex items-center justify-center gap-1 text-[#31A24C] border border-[#31A24C]/40 hover:bg-[#31A24C]/15 rounded px-2 py-1 text-[10px] font-semibold"><RefreshCw size={11} /> Restore</button>
                  <button data-testid={`safety-purge-${it.id}`} onClick={() => purge(it.id)} className="flex items-center justify-center text-[#E60023] border border-[#E60023]/40 hover:bg-[#E60023]/15 rounded px-2 py-1"><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function DiscordImportPanel() {
  const [channelId, setChannelId] = useState("");
  const [tags, setTags] = useState("");
  const [configured, setConfigured] = useState(true);
  const [starting, setStarting] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    api.get("/admin/discord/status").then(({ data }) => setConfigured(data.configured)).catch(() => {});
    return () => clearInterval(pollRef.current);
  }, []);

  const poll = (id) => {
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/admin/discord/import/${id}`);
        setJob(data);
        if (data.status === "completed") { clearInterval(pollRef.current); setStarting(false); }
      } catch { clearInterval(pollRef.current); setStarting(false); }
    }, 3000);
  };

  const start = async () => {
    if (!channelId.trim()) { toast.error("Enter a Discord channel ID"); return; }
    setStarting(true); setJob(null);
    try {
      const { data } = await api.post("/admin/discord/import", { channel_id: channelId.trim(), tags: tags.trim() });
      toast.success(`Importing from #${data.channel_name || "channel"}…`);
      setJob({ status: "running", scanned: 0, stories: 0, media: 0, skipped: 0, failed: 0, stories_blocked: 0, channel_name: data.channel_name });
      poll(data.job_id);
    } catch (err) {
      setStarting(false);
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed to start");
    }
  };

  const Stat = ({ icon: Icon, label, value, color }) => (
    <div className="flex items-center gap-2 bg-[#141414] border border-[#1C1C1F] rounded-xl px-4 py-3">
      <Icon size={16} style={{ color }} />
      <div><p className="text-white font-heading text-xl leading-none">{value}</p><p className="text-[#52525B] text-[11px] font-mono uppercase tracking-wide mt-1">{label}</p></div>
    </div>
  );

  return (
    <div className="max-w-[820px]" data-testid="discord-import-panel">
      <div className="flex items-center gap-3 mb-2">
        <MessageCircle className="text-[#5865F2]" size={24} />
        <h2 className="font-heading text-2xl uppercase tracking-tight text-white">Discord importer</h2>
      </div>
      <p className="text-[#A1A1AA] text-sm mb-6">Pull a channel's <span className="text-white">entire attachment history</span>. Documents (.txt/.pdf/.docx) are AI-scanned into <span className="text-[#00E5FF]">Stories</span>; images &amp; videos go to the <span className="text-[#0066FF]">Media library</span>. The bot token is stored securely on the server.</p>

      {!configured && (
        <div className="flex items-center gap-2 bg-[#E60023]/10 border border-[#E60023]/30 text-[#E60023] rounded-xl px-4 py-3 mb-5 text-sm" data-testid="discord-not-configured">
          <AlertTriangle size={16} /> Bot token isn't configured on the server yet.
        </div>
      )}

      <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5 mb-6">
        <label className="text-[#8A8D91] text-xs font-mono uppercase tracking-wide">Channel ID</label>
        <input
          data-testid="discord-channel-input"
          value={channelId}
          onChange={(e) => setChannelId(e.target.value)}
          placeholder="e.g. 1013212535744376942"
          className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 rounded-xl text-sm mt-1.5 mb-4 focus:outline-none focus:border-[#5865F2] placeholder:text-[#52525B]"
        />
        <label className="text-[#8A8D91] text-xs font-mono uppercase tracking-wide">Extra tags (optional, comma-separated)</label>
        <input
          data-testid="discord-tags-input"
          value={tags}
          onChange={(e) => setTags(e.target.value)}
          placeholder="e.g. imported, archive"
          className="w-full bg-[#141414] border border-[#1C1C1F] text-white px-4 py-3 rounded-xl text-sm mt-1.5 mb-4 focus:outline-none focus:border-[#5865F2] placeholder:text-[#52525B]"
        />
        <button
          data-testid="discord-import-start-btn"
          onClick={start}
          disabled={starting || !configured}
          className="flex items-center gap-2 bg-[#5865F2] hover:bg-[#4752c4] disabled:opacity-50 text-white font-heading uppercase tracking-widest text-xs px-5 py-3 rounded-xl transition-colors"
        >
          {starting ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />} Import channel history
        </button>
      </div>

      {job && (
        <div className="bg-[#0A0A0A] border border-white/[0.06] rounded-2xl p-5" data-testid="discord-job-panel">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-heading uppercase tracking-tight text-lg">
              {job.status === "completed" ? "Import complete" : `Importing${job.channel_name ? ` #${job.channel_name}` : ""}…`}
            </h3>
            {job.status !== "completed" && <Loader2 className="animate-spin text-[#5865F2]" size={18} />}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <Stat icon={Archive} label="Scanned" value={job.scanned} color="#8A8D91" />
            <Stat icon={BookOpen} label="Stories" value={job.stories} color="#00E5FF" />
            <Stat icon={Film} label="Media" value={job.media} color="#0066FF" />
            <Stat icon={Shield} label="Blocked" value={job.stories_blocked} color="#E60023" />
            <Stat icon={AlertTriangle} label="Skipped" value={job.skipped} color="#F1A817" />
            <Stat icon={AlertTriangle} label="Failed" value={job.failed} color="#E60023" />
          </div>
          {job.error && <p className="text-[#E60023] text-xs mt-4">{job.error}</p>}
          {job.stories_blocked > 0 && <p className="text-[#E60023] text-xs mt-4 flex items-center gap-2"><Shield size={13} /> {job.stories_blocked} document(s) blocked for depicting minors — not archived.</p>}
        </div>
      )}
    </div>
  );
}


function BulkUpscaleCard({ testid, icon, title, accent, blurb, data, onStart, onCancel }) {
  const job = data?.job || {};
  const running = job.running;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;
  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid={`${testid}-card`}>
      <div className="flex items-center gap-2 mb-2">{icon}
        <span className="text-white font-heading uppercase tracking-widest text-xs">{title}</span>
      </div>
      <p className="text-[#52525B] text-[11px] mb-3">{blurb}</p>
      <div className="flex items-center gap-2 text-[11px] font-mono text-[#A1A1AA] mb-3">
        <span className="px-2 py-0.5 rounded-full bg-[#141414] border border-[#1C1C1F]">pending {data?.pending ?? "—"}</span>
        <span className="px-2 py-0.5 rounded-full bg-[#141414] border border-[#1C1C1F]">done {data?.upscaled_total ?? "—"}</span>
      </div>
      {running ? (
        <>
          <div className="h-2 rounded-full bg-[#141414] overflow-hidden mb-2">
            <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: accent }} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-mono text-[#A1A1AA]">{job.done}/{job.total} · ✓{job.changed} ·skip {job.skipped}{job.failed ? ` ·fail ${job.failed}` : ""}</span>
            <button data-testid={`${testid}-cancel`} onClick={onCancel} className="flex items-center gap-1 text-[#e5484d] border border-[#e5484d]/40 hover:bg-[#e5484d]/10 rounded-md px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider"><StopCircle size={12} /> Stop</button>
          </div>
        </>
      ) : (
        <button data-testid={`${testid}-start`} onClick={onStart}
          className="inline-flex items-center gap-2 font-heading uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg transition-colors text-black hover:opacity-90"
          style={{ background: accent }}>
          <Maximize2 size={13} /> Start pass
        </button>
      )}
    </div>
  );
}

function UpscalePanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [target, setTarget] = useState("1080p");
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [starting, setStarting] = useState(false);
  const [imgBulk, setImgBulk] = useState(null);
  const [vidBulk, setVidBulk] = useState(null);

  const loadBulk = useCallback(async () => {
    try { const { data } = await api.get("/admin/upscale-all/status"); setImgBulk(data); } catch {}
    try { const { data } = await api.get("/admin/video-upscale-all/status"); setVidBulk(data); } catch {}
  }, []);
  useEffect(() => { loadBulk(); const t = setInterval(loadBulk, 3000); return () => clearInterval(t); }, [loadBulk]);

  const startImgBulk = async () => {
    if (!window.confirm("Upscale EVERY photo in the library (2x, replaces each in place)? Runs in the background.")) return;
    try { const { data } = await api.post("/admin/upscale-all/start"); toast[data.started ? "success" : "info"](data.started ? "Photo upscale pass started" : data.message); loadBulk(); } catch { toast.error("Couldn't start"); }
  };
  const cancelImgBulk = async () => { try { await api.post("/admin/upscale-all/cancel"); toast("Stopping after current item…"); } catch {} };
  const startVidBulk = async () => {
    if (!window.confirm(`Upscale EVERY video to ${target} (replaces each in place)? This is slow and CPU-heavy. Runs in the background.`)) return;
    try { const { data } = await api.post(`/admin/video-upscale-all/start?target=${target}`); toast[data.started ? "success" : "info"](data.started ? "Video upscale pass started" : data.message); loadBulk(); } catch { toast.error("Couldn't start"); }
  };
  const cancelVidBulk = async () => { try { await api.post("/admin/video-upscale-all/cancel"); toast("Stopping after current item…"); } catch {} };

  const search = async () => {
    try { const { data } = await api.get("/admin/media", { params: { filter: "video", q, limit: 30 } }); setResults(data || []); }
    catch { toast.error("Search failed"); }
  };
  const loadStatus = useCallback(async () => {
    try { const { data } = await api.get("/admin/upscale/status"); setJobs(data.jobs || []); setBusy(data.busy); } catch {}
  }, []);
  useEffect(() => { loadStatus(); const t = setInterval(loadStatus, 3000); return () => clearInterval(t); }, [loadStatus]);

  const start = async () => {
    if (!selected) return;
    setStarting(true);
    try {
      await api.post("/admin/upscale", { media_id: selected.id, target });
      toast.success("Upscale started — runs in the background");
      loadStatus();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start"); }
    finally { setStarting(false); }
  };
  const cancel = async (id) => {
    try { await api.post(`/admin/upscale/${id}/cancel`); toast.success("Stopping…"); loadStatus(); } catch {}
  };

  const ACTIVE = ["queued", "downloading", "upscaling", "uploading", "cancelling"];
  const LABELS = { queued: "Queued", downloading: "Downloading source", upscaling: "Upscaling…", uploading: "Saving result", completed: "Done", cancelled: "Stopped", failed: "Failed", cancelling: "Stopping…" };

  return (
    <div className="space-y-4" data-testid="admin-upscale-panel">
      <div className="grid md:grid-cols-2 gap-4">
        <BulkUpscaleCard
          testid="bulk-photo" icon={<ImagePlus size={18} className="text-[#31A24C]" />}
          title="Upscale all photos" accent="#31A24C"
          blurb="2x Lanczos + sharpen on every photo in the library, replacing each in place. Free & on-server."
          data={imgBulk} onStart={startImgBulk} onCancel={cancelImgBulk} />
        <BulkUpscaleCard
          testid="bulk-video" icon={<Film size={18} className="text-[#00E5FF]" />}
          title={`Upscale all videos (${target})`} accent="#00E5FF"
          blurb="Re-encodes every lower-res video up to the chosen height (set it below), replacing each in place. Slow & CPU-heavy."
          data={vidBulk} onStart={startVidBulk} onCancel={cancelVidBulk} />
      </div>

      <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5">
        <div className="flex items-center gap-2 mb-2">
          <Wand2 size={18} className="text-[#00E5FF]" />
          <span className="text-white font-heading uppercase tracking-widest text-xs">Video upscaler</span>
        </div>
        <p className="text-[#52525B] text-xs mb-4">Sharpens & enlarges a video (Lanczos + smart sharpen) on the server. Handles large files (up to ~5&nbsp;GB), runs in the background, and saves the result as a <span className="text-[#A1A1AA]">new post</span> so your original stays untouched.</p>

        <div className="flex gap-2 mb-3">
          <input data-testid="upscale-search" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && search()} placeholder="Search your videos by title / uploader / tag" className="flex-1 bg-[#141414] border border-[#1C1C1F] text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:border-[#00E5FF]" />
          <button data-testid="upscale-search-btn" onClick={search} className="bg-[#141414] border border-[#1C1C1F] text-[#A1A1AA] hover:text-white px-4 rounded-lg text-sm">Search</button>
        </div>

        {results.length > 0 && (
          <div className="max-h-52 overflow-y-auto custom-scrollbar grid grid-cols-2 md:grid-cols-3 gap-2 mb-4" data-testid="upscale-results">
            {results.map((m) => (
              <button key={m.id} data-testid={`upscale-pick-${m.id}`} onClick={() => setSelected(m)}
                className={`text-left rounded-lg overflow-hidden border transition-colors ${selected?.id === m.id ? "border-[#00E5FF]" : "border-[#1C1C1F] hover:border-[#2A2A2E]"}`}>
                <div className="aspect-video bg-black relative">
                  {m.thumb_url && <img src={m.thumb_url} alt="" className="w-full h-full object-cover" />}
                  <span className="absolute top-1 left-1 bg-black/70 rounded px-1"><Film size={10} className="text-white" /></span>
                </div>
                <div className="p-1.5 text-[11px] text-white truncate">{m.title || "Untitled"}</div>
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-1.5">
            {Object.keys({ "720p": 0, "1080p": 0, "1440p": 0, "2160p": 0 }).map((t) => (
              <button key={t} data-testid={`upscale-target-${t}`} onClick={() => setTarget(t)}
                className={`px-3 py-1.5 rounded-md text-[11px] font-heading uppercase tracking-wider transition-colors ${target === t ? "bg-[#00E5FF] text-black" : "bg-[#141414] text-[#A1A1AA] hover:text-white"}`}>
                {t === "2160p" ? "4K" : t}
              </button>
            ))}
          </div>
          <button data-testid="upscale-start" onClick={start} disabled={!selected || busy || starting}
            className="ml-auto inline-flex items-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg hover:bg-[#00c9e0] transition-colors disabled:opacity-40">
            {starting ? <Loader2 className="animate-spin" size={13} /> : <Wand2 size={13} />}
            {busy ? "Busy…" : selected ? `Upscale “${(selected.title || "video").slice(0, 20)}”` : "Pick a video"}
          </button>
        </div>
      </div>

      {jobs.length > 0 && (
        <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid="upscale-jobs">
          <span className="text-white font-heading uppercase tracking-widest text-xs">Recent upscales</span>
          <div className="mt-3 space-y-2">
            {jobs.map((j) => {
              const active = ACTIVE.includes(j.status);
              return (
                <div key={j.id} data-testid={`upscale-job-${j.id}`} className="flex items-center gap-3 bg-[#141414] rounded-lg px-3 py-2">
                  {active ? <Loader2 size={15} className="animate-spin text-[#00E5FF]" /> : j.status === "completed" ? <Check size={15} className="text-[#3fb950]" /> : <Clock size={15} className="text-[#e57373]" />}
                  <div className="min-w-0 flex-1">
                    <div className="text-white text-sm truncate">{j.source_title} <span className="text-[#52525B] text-[11px]">→ {j.target === "2160p" ? "4K" : j.target}</span></div>
                    <div className="text-[11px] font-mono text-[#A1A1AA]">{LABELS[j.status] || j.status}{j.output_size ? ` · ${fmtBytes(j.output_size)}` : ""}{j.error ? ` · ${j.error}` : ""}</div>
                  </div>
                  {j.status === "completed" && j.new_media_id && (
                    <a href={`/media/${j.new_media_id}`} className="text-[#00E5FF] hover:text-white text-[11px] font-semibold whitespace-nowrap">View</a>
                  )}
                  {active && (
                    <button data-testid={`upscale-cancel-${j.id}`} onClick={() => cancel(j.id)} className="text-[#e5484d] hover:text-[#ff6b6b]"><StopCircle size={16} /></button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function CompressionCard() {
  const [st, setSt] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/compress/status"); setSt(data); } catch {}
  }, []);

  useEffect(() => {
    load();
    const running = st?.job?.running;
    const t = setInterval(load, running ? 3000 : 10000);
    return () => clearInterval(t);
  }, [load, st?.job?.running]);

  const start = async () => {
    setStarting(true);
    try {
      const { data } = await api.post("/admin/compress/backfill");
      if (data.started) toast.success("Compression started — running in the background");
      else toast.info(data.message || "Already running");
      load();
    } catch { toast.error("Couldn't start compression"); }
    finally { setStarting(false); }
  };

  if (!st) return null;
  const { job, library } = st;
  const pct = job.total ? Math.round((job.done / job.total) * 100) : 0;

  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid="admin-compress-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Archive size={18} className="text-[#00E5FF]" />
          <span className="text-white font-heading uppercase tracking-widest text-xs">Storage compression</span>
        </div>
        <button
          data-testid="compress-start"
          onClick={start}
          disabled={starting || job.running || library.pending === 0}
          className="inline-flex items-center gap-2 bg-[#00E5FF] text-black font-heading uppercase tracking-widest text-[11px] px-4 py-2 rounded-lg hover:bg-[#00c9e0] transition-colors disabled:opacity-40"
        >
          {job.running ? <><Loader2 className="animate-spin" size={13} /> Running</> : library.pending === 0 ? <><Check size={13} /> All done</> : <><Archive size={13} /> Compress library</>}
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <StatCard label="Storage Used" value={fmtBytes(library.used_bytes)} icon={HardDrive} sub={library.unmeasured > 0 ? `${library.measured} of ${library.total_media} measured` : "across your library"} />
        <StatCard label="Total Saved" value={fmtBytes(library.total_saved_bytes)} icon={Archive} sub="freed by compression" />
        <StatCard label="Compressed" value={library.compressed} icon={Check} sub="items shrunk" />
        <StatCard label="Pending" value={library.pending} icon={Clock} sub="not yet processed" />
      </div>

      {job.running && (
        <div data-testid="compress-progress">
          <div className="flex items-center justify-between mb-1.5 text-[11px] font-mono">
            <span className="text-[#00E5FF]">Compressing {job.done}/{job.total}</span>
            <span className="text-[#52525B]">saved {fmtBytes(job.saved_bytes)} this run</span>
          </div>
          <div className="h-2 bg-[#141414] rounded-full overflow-hidden">
            <div className="h-full bg-[#00E5FF] rounded-full transition-all duration-500 shadow-[0_0_10px_#00E5FF]" style={{ width: `${pct}%` }} />
          </div>
        </div>
      )}
      {!job.running && library.pending > 0 && (
        <p className="text-[#52525B] text-xs">Aggressively re-encodes videos to 720p H.264 and images to compressed JPEG, replacing the stored originals to free space. Already-efficient clips are skipped.</p>
      )}
    </div>
  );
}

function TrendChart({ title, data, series, domain, unit = "", line = false, testid }) {
  const Chart = line ? LineChart : AreaChart;
  return (
    <div className="bg-[#0A0A0A] border border-[#1C1C1F] rounded-2xl p-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[#A1A1AA] text-xs font-heading uppercase tracking-widest">{title}</span>
        <div className="flex items-center gap-3">
          {series.map((se) => (
            <span key={se.key} className="flex items-center gap-1.5 text-[10px] font-mono text-[#52525B]">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: se.color, boxShadow: `0 0 6px ${se.color}` }} /> {se.label}
            </span>
          ))}
        </div>
      </div>
      <div className="h-[160px]" style={{ minHeight: 160 }}>
        {data.length < 2 ? (
          <div className="h-full flex items-center justify-center text-[#52525B] text-[11px] font-mono">collecting data…</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <Chart data={data} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <defs>
                {series.map((se) => (
                  <linearGradient key={se.key} id={`grad-${se.key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={se.color} stopOpacity={0.5} />
                    <stop offset="100%" stopColor={se.color} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
              <YAxis domain={domain || [0, "auto"]} tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} width={40} />
              <Tooltip
                contentStyle={{ background: "#0A0A0A", border: "1px solid #1C1C1F", borderRadius: 12, fontSize: 11 }}
                labelStyle={{ color: "#A1A1AA" }} itemStyle={{ padding: 0 }}
                formatter={(v, n) => [`${v}${unit}`, n]} />
              {series.map((se) => line ? (
                <Line key={se.key} type="monotone" dataKey={se.key} name={se.label} stroke={se.color} strokeWidth={2} dot={false} isAnimationActive={false} />
              ) : (
                <Area key={se.key} type="monotone" dataKey={se.key} name={se.label} stroke={se.color} strokeWidth={2} fill={`url(#grad-${se.key})`} dot={false} isAnimationActive={false} />
              ))}
            </Chart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}

